<template>
  <div class="home-shell">
    <!-- App loading overlay -->
    <Transition name="app-loading">
      <div v-if="!isAppReady" class="app-loading-overlay">
        <div class="app-loading-box">
          <div class="app-loading-spinner"></div>
          <span class="app-loading-label">Carregando…</span>
        </div>
      </div>
    </Transition>

    <!-- Suspension overlay for suspended obra admins -->
    <div v-if="isSuspendedAdmin" class="suspension-overlay">
      <div class="suspension-panel">
        <div class="suspension-header">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#92400e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <h1 class="suspension-title">Esta obra esta suspensa</h1>
        </div>
        <p class="suspension-subtitle">Seu acesso esta restrito. Para retomar as operacoes, solicite a reativacao ao administrador master.</p>

        <div class="suspension-stats">
          <div class="susp-stat">
            <span class="susp-stat-value">{{ accounts.length }}</span>
            <span class="susp-stat-label">Usuarios</span>
          </div>
          <div class="susp-stat">
            <span class="susp-stat-value">{{ projects.length }}</span>
            <span class="susp-stat-label">Projetos</span>
          </div>
        </div>

        <div v-if="!reactivationSent" class="suspension-form">
          <label class="susp-label">Mensagem (opcional)</label>
          <textarea v-model="reactivationMessage" class="susp-textarea" rows="3" placeholder="Descreva o motivo da solicitacao..."></textarea>
          <p v-if="reactivationError" class="susp-error">{{ reactivationError }}</p>
          <button class="susp-btn" type="button" :disabled="reactivationLoading" @click="submitReactivation">
            {{ reactivationLoading ? 'Enviando...' : 'Solicitar Reativacao' }}
          </button>
        </div>
        <div v-else class="susp-success">
          Solicitacao enviada com sucesso! Aguarde o contato do administrador.
        </div>

        <button class="susp-logout" type="button" @click="logout">Sair</button>
      </div>
    </div>

    <div v-if="isSystemSaving" class="saving-overlay">
      <div class="saving-card">Salvando alteracoes...</div>
    </div>
    <DashboardNavbar
      :site-name="siteName"
      :site-logo-url="siteLogoUrl"
      :search-query="searchQuery"
      :user-name="userName"
      :user-avatar="userAvatar"
      :notification-count="unreadNotifications"
      :show-menu-button="isNarrowScreen"
      :menu-open="mobileMenuOpen"
      :logo-text="logoText"
      :user-username="currentUsername"
      :ia-mode="false"
      :show-ia-button="showIaButton"
      @toggle-menu="toggleMobileMenu"
      @search-change="onSearchChange"
      @profile="goProfile"
      @logout="logout"
      @notifications="toggleNotifications"
      @whats-new="showWhatsNew = true"
      @ia-mode="router.push('/ia')"
      @anotacoes="router.push('/anotacoes')"
    />

    <!-- Storage alert banner -->
    <div v-if="showStorageAlert" class="storage-alert-banner" :class="storageAlertPct >= 90 ? 'storage-alert-critical' : 'storage-alert-warn'">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
      <span>Armazenamento: <strong>{{ storageAlertPct }}%</strong> utilizado ({{ storageAlertMb.toFixed(1) }} MB / {{ storageAlertLimit }} MB). {{ storageAlertPct >= 90 ? 'Limite crítico!' : 'Considere limpar arquivos antigos.' }}</span>
      <button class="storage-alert-dismiss" type="button" @click="storageAlertDismissed = true">✕</button>
    </div>

    <div class="home-body">
      <transition name="backdrop-fade">
        <div v-if="isNarrowScreen && mobileMenuOpen" class="mobile-sidebar-backdrop" @click="toggleMobileMenu"></div>
      </transition>
      <div class="sidebar-shell" :class="{ open: !isNarrowScreen || mobileMenuOpen }">
        <DashboardSidebar
          :user-name="userName"
          :system-name="siteName"
          :is-admin="isAdmin"
          :is-master-admin="isMasterAdmin"
          :is-analyst="isAnalyst"
          :can-manage-warehouse="canManageWarehouse"
          :can-request-warehouse="canRequestWarehouse"
          :active-path="normalizedActivePath"
          :current-mode="currentMode"
          :has-subordinates="hasSubordinates"
          :show-search="isNarrowScreen"
          :search-query="searchQuery"
          :ticket-pending-count="ticketPendingCount"
          :messenger-unread="messengerStore.state.unreadTotal"
          :project-name="panel === 'projectWorkspace' ? panelTitle : ''"
          :panel-title="panel !== 'projectWorkspace' ? panelTitle : ''"
          :maintenance-enabled="Boolean(systemStore.state.config.maintenanceEnabled || systemForm.maintenanceEnabled)"
          :can-manage-construction="canManageConstruction"
          :can-manage-maintenance="canManageMaintenance"
          :can-assign-maintenance="maintenanceAccessLevel >= 2"
          :can-manage-office="canManageOffice"
          :can-manage-portaria="canManagePortaria"
          :show-tickets="showTickets"
          @messenger="toggleMessenger"
          @open-create-popup="showCreatePopup = true"
          @start-report="startReport"
          @dialogo="goDialogo"
          @permissao="goPermissao"
          @ocorrido="goOcorrido"
          @overview="openOverview"
          @documents="openDocuments"
          @projects="openProjects"
          @users="openUsers"
          @system="openSystem"
          @my-projects="openMyProjects"
          @my-tasks="openMyTasks"
          @drawings="openDrawings"
          @warehouse-stock="openWarehouseStock"
          @warehouse-requests="openWarehouseRequests"
          @warehouse-planejamento="openWarehousePlanejamento"
          @warehouse-avarias="openWarehouseAvarias"
          @warehouse-nfe-import="openWarehouseNfeImport"
          @warehouse-request-item="openWarehouseRequestItem"
          @heavy-machinery="openHeavyMachinery"
          :warehouse-tab="warehouseTab"
          @tickets="openTickets"
          @activity-log="openActivityLog"
          @filter="setFilter"
          @search-change="onSearchChange"
          @search-submit="onSearchSubmit"
          @start-atividade="openAtividadeModal"
          @start-pendencia="openPendenciaModal"
          @maintenance="openMaintenance"
          @maintenance-manuals="openMaintenanceManuals"
          @maintenance-service-orders="openMaintenanceServiceOrders"
          @maintenance-os-consulta="openMaintenanceOSConsulta"
          @go-geral="openGeral"
          @go-admin-mode="openAdminMode"
          @go-warehouse="openWarehouseMode"
          @go-office="openOfficeMode"
          @go-portaria="openPortariaMode"
          @office-tab="setOfficeTab"
          :office-tab="officeTab"
        />
      </div>

      <main class="content" :class="{ 'workspace-mode': panel === 'projectWorkspace', 'profile-mode': panel === 'profilePanel', 'maintenance-mode': panel === 'maintenancePanel' || panel === 'maintenanceProjectPanel' || panel === 'maintenanceManualsPanel', 'form-mode': panel === 'reportForm' || panel === 'dialogForm' || panel === 'occurrenceForm' }">
        <OverviewPanel
          v-if="panel === 'overview'"
          :projects="projects"
          :project-insights="projectInsights"
          :accounts="accounts"
          :format-report-date="formatReportDate"
          @refresh="refreshAllData"
          @extend-project="extendProject"
        />

        <ReportsPanel
          v-else-if="panel === 'list'"
          :filter-type="filterType"
          :user-filter="userFilter"
          :user-options="userOptions"
          :date-from="dateFrom"
          :date-to="dateTo"
          :all-types="allTypes"
          :selected-items="selectedReports"
          :filtered-items="filteredItems"
          :format-report-date="formatReportDate"
          :activities-text="activitiesText"
          @set-filter-type="filterType = $event"
          @set-user-filter="userFilter = $event"
          @set-date-from="dateFrom = $event"
          @set-date-to="dateTo = $event"
          @toggle-create="showCreatePopup = true"
          @export-docs="exportSelectedReportsAsDoc"
          @export-pdfs="exportSelectedReportsAsPdf"
          @delete-docs="askDeleteSelectedReports"
          @toggle-item="toggleReportSelection"
          @view="openView"
          @edit="openEdit"
        />

        <MaintenanceOverviewPanel
          v-else-if="panel === 'maintenancePanel'"
          :is-admin="isAdmin"
          :access-level="maintenanceAccessLevel"
        />
        <MaintenanceProjectPanel
          v-else-if="panel === 'maintenanceProjectPanel' && maintenanceProject"
          :project="maintenanceProject"
          :is-admin="isAdmin"
          :access-level="maintenanceAccessLevel"
          @back="openMaintenance"
        />
        <MaintenanceManualsPanel
          v-else-if="panel === 'maintenanceManualsPanel'"
          :is-admin="isAdmin"
        />
        <MaintenanceServiceOrdersPanel
          v-else-if="panel === 'maintenanceServiceOrdersPanel'"
          :access-level="maintenanceAccessLevel"
        />
        <MaintenanceOSConsultaPanel
          v-else-if="panel === 'maintenanceOSConsultaPanel'"
        />
        <PreencherOSPanel
          v-else-if="panel === 'preencherOSPanel'"
        />
        <PortariaOverviewPanel
          v-else-if="panel === 'portariaPanel'"
          :access-level="portariaAccessLevel"
        />

        <div v-else class="panel">
          <div
            v-if="panel === 'projects' || (panel !== 'projectWorkspace' && panel !== 'documents' && panel !== 'drawings' && panel !== 'users' && panel !== 'system' && panel !== 'warehousePanel' && panel !== 'warehouseRequestPanel' && panel !== 'heavyMachineryPanel' && panel !== 'ticketsPanel' && panel !== 'profilePanel' && panel !== 'officePanel' && panel !== 'anotacoesPanel' && panel !== 'reportForm' && panel !== 'dialogForm' && panel !== 'occurrenceForm')"
            class="content-header"
          >
            <div class="actions">
            </div>
          </div>

          <SubmissionFormView
            v-if="panel === 'reportForm'"
            :embedded="true"
            :submission-id="activeId"
            :prefill-type="prefillType"
            @done="backToList"
            @cancel="backToList"
            @edit="openEdit"
          />
          <SafetyDialogFormView
            v-else-if="panel === 'dialogForm'"
            :embedded="true"
            :submission-id="activeId"
            @done="backToList"
            @cancel="backToList"
          />
          <SafetyOccurrenceFormView
            v-else-if="panel === 'occurrenceForm'"
            :embedded="true"
            :submission-id="activeId"
            @done="backToList"
            @cancel="backToList"
          />
          <SubmissionView
            v-else-if="panel === 'reportView'"
            :embedded="true"
            :submission-id="activeId"
            @back="backToList"
            @edit="openEdit"
          />
          <SafetyDocumentView
            v-else-if="panel === 'safetyView'"
            :embedded="true"
            :submission-id="activeId"
            @back="backToList"
            @edit="openEdit"
          />
          <DocumentsPanel
            v-else-if="panel === 'documents'"
            :all-types="allTypes"
            :doc-filter-type="docFilterType"
            :doc-user-filter="docUserFilter"
            :doc-date-from="docDateFrom"
            :doc-date-to="docDateTo"
            :doc-user-options="docUserOptions"
            :selected-docs="selectedDocs"
            :documents-items="documentsItems"
            :format-report-date="formatReportDate"
            :activities-text="activitiesText"
            @set-doc-type="docFilterType = $event"
            @set-doc-user="docUserFilter = $event"
            @set-doc-date-from="docDateFrom = $event"
            @set-doc-date-to="docDateTo = $event"
            @export-docs="exportSelectedDocsAsDoc"
            @export-pdfs="exportSelectedDocsAsPdf"
            @delete-docs="askDeleteSelectedDocs"
            @toggle-doc="toggleDoc"
            @view="openView"
            @edit="openEdit"
          />
          <DrawingsPanel
            v-else-if="panel === 'drawings'"
            :projects="projects"
          />
          <UsersPanel
            v-else-if="panel === 'users'"
            :accounts="accounts"
            :current-uid="currentUid"
            :current-user-role="sessionUserRole"
            :show-account-form="showAccountForm"
            :account-form="accountForm"
            :account-error="accountError"
            :roles="roles"
            :show-role-form="showRoleForm"
            :role-form="roleForm"
            :role-error="roleError"
            :offline-employees="offlineEmployees"
            :show-offline-employee-form="showOfflineEmployeeForm"
            :offline-employee-form="offlineEmployeeForm"
            :offline-employee-error="offlineEmployeeError"
            :account-subordinate-options="accountSubordinateOptions"
            :is-analyst="isAnalyst"
            @refresh-accounts="refreshAccounts"
            @open-account-form="openAccountForm"
            @edit-account="editAccount"
            @delete-account="askDeleteAccount"
            @save-account="saveAccount"
            @close-account-form="closeAccountForm"
            @update-account-form="onAccountFormUpdate"
            @add-account-subordinate="addAccountSubordinate"
            @remove-account-subordinate="removeAccountSubordinate"
            @refresh-roles="refreshRoles"
            @open-role-form="openRoleForm"
            @edit-role="editRole"
            @save-role="saveRole"
            @close-role-form="closeRoleForm"
            @add-role-subordinate="addRoleSubordinate"
            @remove-role-subordinate="removeRoleSubordinate"
            @update-role-form="onRoleFormUpdate"
            @open-offline-employee-form="openOfflineEmployeeForm"
            @close-offline-employee-form="closeOfflineEmployeeForm"
            @save-offline-employee="saveOfflineEmployee"
            @edit-offline-employee="editOfflineEmployee"
            @delete-offline-employee="askDeleteOfflineEmployee"
            @update-offline-employee-form="onOfflineEmployeeFormUpdate"
          />
          <ProjectsPanel
            v-else-if="panel === 'projects'"
            :projects="projects"
            :project-mode="projectMode"
            :selected-project="selectedProject"
            :project-form="projectForm"
            :project-error="projectError"
            :accounts="accounts"
            :format-report-date="formatReportDate"
            :is-analyst="isAnalyst"
            @view-project="viewProject"
            @edit-project="editProject"
            @delete-project="askDeleteProject"
            @close-project-mode="closeProjectMode"
            @save-project="saveProject"
            @update-project-form="onProjectFormUpdate"
            @add-responsible="addResponsibleUser"
            @remove-responsible="removeResponsibleUser"
          />
          <MyProjectsPanel
            v-else-if="panel === 'myProjects'"
            :projects="projects"
            :project-insights="projectInsights"
            :accounts="accounts"
            :project-mode="projectMode"
            :project-form="projectForm"
            :project-error="projectError"
            :selected-project="selectedProject"
            :is-admin="isAdmin"
            :current-username="currentUsername"
            :subordinate-options="subordinateOptions"
            :format-report-date="formatReportDate"
            @view-project="viewProject"
            @extend-project="extendProject"
            @edit-project="editProject"
            @close-project-mode="closeProjectMode"
            @save-project="saveProject"
            @update-project-form="onProjectFormUpdate"
            @add-responsible="addResponsibleUser"
            @remove-responsible="removeResponsibleUser"
            @add-temp-responsible="addTempResponsible"
            @remove-temp-responsible="removeTempResponsible"
            @update-temp-responsible="updateTempResponsible"
          />
          <MyTasksPanel
            v-else-if="panel === 'myTasks'"
            :projects="projects"
            :current-username="currentUsername"
            :accounts="accounts"
            @view-project="viewProject"
          />
          <ProjectWorkspaceView
            v-else-if="panel === 'projectWorkspace'"
            :embedded="true"
            :project-id="selectedProjectId"
            :is-analyst="isAnalyst"
            :system-name="siteName"
            :logo-text="logoText"
            @back="backToProjectsPanel"
          />
          <WarehouseView v-else-if="panel === 'warehousePanel'" :is-analyst="isAnalyst" />
          <WarehouseRequestItemView v-else-if="panel === 'warehouseRequestPanel'" />
          <OfficePanel v-else-if="panel === 'officePanel'" :active-tab="officeTab" @tab-change="setOfficeTab" />
          <HeavyMachineryView v-else-if="panel === 'heavyMachineryPanel'" :is-analyst="isAnalyst" />
          <TicketsView v-else-if="panel === 'ticketsPanel'" />
          <ProfileEditView v-else-if="panel === 'profilePanel'" :in-panel="true" @done="panel = 'overview'" />
          <AnotacoesView v-else-if="panel === 'anotacoesPanel'" :embedded="true" />
          <NotificationsView v-else-if="panel === 'notificationsPanel'" :embedded="true" />
          <SystemPanel
            v-else-if="panel === 'system'"
            :form="systemForm"
            :error-text="systemError"
            :success-text="systemSuccess"
            :initial-tab="systemTab"
            :is-master-admin="isMasterAdmin"
            @update-form="updateSystemForm"
            @save="saveSystem"
          />
          <div v-else class="item-card">
            <h3 class="item-title">Problema seu</h3>
          </div>
        </div>
      </main>
    </div>

    <ConfirmDeleteModal :open="confirmDeleteOpen" :label="confirmDeleteLabel" :saving="confirmDeleteSaving" @confirm="confirmDelete" @cancel="cancelDelete" />
    <OnboardingWizard
      v-if="showOnboarding"
      :system-name="siteName"
      :has-project="projects.length > 0"
      :has-user="accounts.length > 1"
      @dismiss="dismissOnboarding"
      @go-to="onboardingGoTo"
    />
    <NotificationsPanel
      v-if="showNotifications"
      :notifications="notifications"
      @close="toggleNotifications"
      @open-notification="openNotification"
      @view-all="goToAllNotifications"
    />
    <div v-if="ddsConfirmOpen" class="dds-overlay">
      <div class="dds-modal">
        <h3>Confirmacao de Presenca DDS</h3>
        <p class="subtitle">
          Voce confirma presenca no DDS "{{ ddsConfirmName }}"?
        </p>
        <video ref="ddsVideoRef" class="dds-video" autoplay playsinline muted></video>
        <div class="actions">
          <button class="btn btn-ghost" type="button" @click="startDdsCamera">Ativar camera</button>
          <button class="btn btn-secondary" type="button" @click="captureDdsPhoto">Capturar foto</button>
          <button class="btn btn-primary" type="button" @click="confirmDdsPresence">Confirmar</button>
          <button class="btn btn-accent" type="button" @click="closeDdsConfirm">Cancelar</button>
        </div>
        <img v-if="ddsPhotoDataUrl" class="dds-preview" :src="ddsPhotoDataUrl" alt="preview camera" />
        <p v-if="ddsConfirmError" class="subtitle" style="color:#a54b2a;">{{ ddsConfirmError }}</p>
      </div>
    </div>

    <MessengerPanel
      :open="messengerOpen"
      :current-username="currentUsername"
      :current-system-id="currentSystemId"
      @close="messengerOpen = false"
    />

    <WhatsNewModal v-if="showWhatsNew" @close="closeWhatsNew" />

    <!-- ── POPUP CRIAR ── -->
    <CreatePopupModal
      :open="showCreatePopup"
      :current-mode="currentMode"
      :is-admin="isAdmin"
      :can-manage-construction="canManageConstruction"
      :can-manage-maintenance="canManageMaintenance"
      :can-manage-warehouse="canManageWarehouse"
      :can-manage-office="canManageOffice"
      @close="showCreatePopup = false"
      @action="handleCreateAction"
    />

    <!-- ── CRIAR ATIVIDADE MODAL ── -->
    <Teleport to="body">
      <div v-if="showAtividadeModal" class="quick-modal-overlay" @click.self="showAtividadeModal = false">
        <div class="quick-modal">
          <div class="quick-modal-header">
            <h3 class="quick-modal-title">Criar atividade</h3>
            <button class="quick-modal-close" type="button" @click="showAtividadeModal = false">✕</button>
          </div>
          <div class="quick-modal-body">
            <p class="quick-modal-sub">Selecione o projeto onde a atividade será criada:</p>
            <div class="quick-project-list">
              <button
                v-for="p in projects"
                :key="p.id"
                class="quick-project-btn"
                type="button"
                @click="goCreateAtividade(p.id)"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
                {{ p.name }}
              </button>
            </div>
          </div>
          <div class="quick-modal-footer">
            <button class="btn btn-ghost" type="button" @click="showAtividadeModal = false">Cancelar</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- ── REGISTRAR PENDÊNCIA MODAL ── -->
    <Teleport to="body">
      <div v-if="showPendenciaModal" class="quick-modal-overlay" @click.self="closePendenciaModal">
        <div class="quick-modal quick-modal--form">
          <div class="quick-modal-header">
            <h3 class="quick-modal-title">Registrar pendência</h3>
            <button class="quick-modal-close" type="button" @click="closePendenciaModal">✕</button>
          </div>
          <div class="quick-modal-body">
            <div class="quick-form-field">
              <label class="quick-form-label">Projeto <span class="quick-required">*</span></label>
              <select v-model="pendenciaForm.projectId" class="quick-form-input" @change="onPendenciaProjectChange">
                <option value="">Selecione um projeto</option>
                <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
            <div class="quick-form-field">
              <label class="quick-form-label">Responsável</label>
              <select v-model="pendenciaForm.responsavel" class="quick-form-select">
                <option value="">Selecione o responsável</option>
                <option v-for="u in accounts" :key="u.uid" :value="u.name">{{ u.name }}</option>
              </select>
            </div>
            <div class="quick-form-field">
              <label class="quick-form-label">Descrição curta <span class="quick-required">*</span></label>
              <input v-model="pendenciaForm.shortDescription" class="quick-form-input" type="text" placeholder="Resumo da pendência" />
            </div>
            <div class="quick-form-field">
              <label class="quick-form-label">Descrição completa</label>
              <textarea v-model="pendenciaForm.fullDescription" class="quick-form-input quick-form-textarea" rows="3" placeholder="Detalhes, contexto, ações necessárias…"></textarea>
            </div>
            <div class="quick-form-field">
              <label class="quick-form-label">Foto (opcional)</label>
              <div
                class="quick-image-grid"
                :class="{ 'is-drag-over': pendenciaPhotoDragOver }"
                @dragenter.prevent="pendenciaPhotoDragOver = true"
                @dragover.prevent="pendenciaPhotoDragOver = true"
                @dragleave.prevent="pendenciaPhotoDragOver = false"
                @drop.prevent="onPendenciaPhotoDrop"
              >
                <div v-if="pendenciaForm.photo" class="quick-image-preview">
                  <img :src="pendenciaForm.photo" alt="Preview" />
                  <button type="button" class="quick-image-remove" @click="pendenciaForm.photo = ''">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                <button v-if="!pendenciaForm.photo" class="quick-image-add" type="button" @click="pendenciaPhotoInputRef?.click()">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M9 4v10M4 9h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>
                </button>
              </div>
              <input ref="pendenciaPhotoInputRef" type="file" accept="image/*" style="display:none" @change="onPendenciaPhoto" />
            </div>
            <p v-if="pendenciaError" class="quick-form-error">{{ pendenciaError }}</p>
          </div>
          <div class="quick-modal-footer">
            <button class="btn btn-ghost" type="button" @click="closePendenciaModal">Cancelar</button>
            <button class="btn btn-primary" type="button" :disabled="pendenciaSaving" @click="submitPendencia">
              {{ pendenciaSaving ? 'Registrando…' : 'Registrar' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { sessionStore } from '@/store/session'
import { submissionsStore, type Submission } from '@/store/submissions'
import { accountsStore, type Account, type AccountRole } from '@/store/accounts'
import { rolesStore, type Role } from '@/store/roles'
import { projectsStore, type Project } from '@/store/projects'
import { systemStore, type SystemConfig } from '@/store/system'
import { offlineEmployeesStore, type OfflineEmployee } from '@/store/offlineEmployees'
import { notificationsStore } from '@/store/notifications'
import { ticketsStore } from '@/store/tickets'
import { apiFetch } from '@/store/api'
import { formatDateOnly } from '@/utils/date'
import { birthDateError } from '@/utils/birthDate'
import { resolveUrl } from '@/utils/resolveUrl'
import SubmissionFormView from '@/views/SubmissionFormView.vue'
import SafetyDialogFormView from '@/views/SafetyDialogFormView.vue'
import SafetyOccurrenceFormView from '@/views/SafetyOccurrenceFormView.vue'
import SubmissionView from '@/views/SubmissionView.vue'
import SafetyDocumentView from '@/views/SafetyDocumentView.vue'
import DashboardNavbar from '@/components/dashboard/DashboardNavbar.vue'
import DashboardSidebar from '@/components/dashboard/DashboardSidebar.vue'
import ConfirmDeleteModal from '@/components/dashboard/ConfirmDeleteModal.vue'
import NotificationsPanel from '@/components/dashboard/NotificationsPanel.vue'
import OverviewPanel from '@/components/dashboard/OverviewPanel.vue'
import ReportsPanel from '@/components/dashboard/ReportsPanel.vue'
import DocumentsPanel from '@/components/dashboard/DocumentsPanel.vue'
import UsersPanel from '@/components/dashboard/UsersPanel.vue'
import ProjectsPanel from '@/components/dashboard/ProjectsPanel.vue'
import MyProjectsPanel from '@/components/dashboard/MyProjectsPanel.vue'
import MyTasksPanel from '@/components/dashboard/MyTasksPanel.vue'
import DrawingsPanel from '@/components/dashboard/DrawingsPanel.vue'
import SystemPanel from '@/components/dashboard/SystemPanel.vue'
import OnboardingWizard from '@/components/dashboard/OnboardingWizard.vue'
import ProjectWorkspaceView from '@/views/ProjectWorkspaceView.vue'
import WarehouseView from '@/views/WarehouseView.vue'
import WarehouseRequestItemView from '@/views/WarehouseRequestItemView.vue'
import HeavyMachineryView from '@/views/HeavyMachineryView.vue'
import TicketsView from '@/views/TicketsView.vue'
import ProfileEditView from '@/views/ProfileEditView.vue'
import MessengerPanel from '@/components/messenger/MessengerPanel.vue'
import MaintenanceOverviewPanel from '@/components/maintenance/MaintenanceOverviewPanel.vue'
import MaintenanceProjectPanel from '@/components/maintenance/MaintenanceProjectPanel.vue'
import MaintenanceManualsPanel from '@/components/maintenance/MaintenanceManualsPanel.vue'
import MaintenanceServiceOrdersPanel from '@/components/maintenance/MaintenanceServiceOrdersPanel.vue'
import MaintenanceOSConsultaPanel from '@/components/maintenance/MaintenanceOSConsultaPanel.vue'
import PreencherOSPanel from '@/components/maintenance/PreencherOSPanel.vue'
import { maintenanceStore } from '@/store/maintenance'
import OfficePanel from '@/components/office/OfficePanel.vue'
import PortariaOverviewPanel from '@/components/portaria/PortariaOverviewPanel.vue'
import AnotacoesView from '@/views/AnotacoesView.vue'
import NotificationsView from '@/views/NotificationsView.vue'
import { timezoneStore } from '@/store/timezone'
import { messengerStore } from '@/store/messenger'
import WhatsNewModal from '@/components/WhatsNewModal.vue'
import CreatePopupModal from '@/components/dashboard/CreatePopupModal.vue'
import { versionStore } from '@/store/versionStore'
import { uiStore } from '@/store/ui'
import { sseOn, sseDisconnect } from '@/store/sse'
import { useReportExport } from '@/composables/useReportExport'
import { submissionEditState } from '@/store/submissionEditState'

type FormFieldValue = string | boolean | number

function toErrorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

export default defineComponent({
  name: 'DashboardView',
  components: {
    DashboardNavbar,
    DashboardSidebar,
    ConfirmDeleteModal,
    NotificationsPanel,
    OverviewPanel,
    ReportsPanel,
    DocumentsPanel,
    UsersPanel,
    ProjectsPanel,
    MyProjectsPanel,
    MyTasksPanel,
    DrawingsPanel,
    SystemPanel,
    ProjectWorkspaceView,
    WarehouseView,
    WarehouseRequestItemView,
    HeavyMachineryView,
    TicketsView,
    ProfileEditView,
    SubmissionFormView,
    SafetyDialogFormView,
    SafetyOccurrenceFormView,
    SubmissionView,
    SafetyDocumentView,
    MessengerPanel,
    MaintenanceOverviewPanel,
    MaintenanceProjectPanel,
    MaintenanceManualsPanel,
    MaintenanceServiceOrdersPanel,
    MaintenanceOSConsultaPanel,
    PreencherOSPanel,
    OnboardingWizard,
    WhatsNewModal,
    OfficePanel,
    PortariaOverviewPanel,
    AnotacoesView,
    NotificationsView,
    CreatePopupModal
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const systemError = ref('')
    const systemSuccess = ref('')
    const isSystemSaving = ref(false)
    const systemForm = ref<SystemConfig>({
      systemId: 'relatorioshow',
      systemUsername: 'relatorioshow',
      systemUsernameChangedAt: '',
      siteName: 'RelatorioShow',
      siteLogoUrl: '',
      loginBackgroundUrl: '',
      usePrimaryLoginBackground: true,
      colors: {
        primary: '#4f3300',
        secondary: '#f3ede3'
      },
      autoSecondary: true,
      attendanceStartTime: '07:00',
      workedWeekdays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      offlineTimeoutDays: 30,
      ganttReportMode: 'ia'
    })
    const showCreatePopup = ref(false)
    const showFilter = ref(false)
    const searchQuery = ref('')
    const filterType = ref('')
    const userFilter = ref('')
    const dateFrom = ref('')
    const dateTo = ref('')
    const currentTitle = ref('Todos os relatorios')
    const panel = ref<
      | 'overview'
      | 'list'
      | 'reportForm'
      | 'dialogForm'
      | 'occurrenceForm'
      | 'reportView'
      | 'safetyView'
      | 'permissao'
      | 'documents'
      | 'drawings'
      | 'users'
      | 'projects'
      | 'myProjects'
      | 'myTasks'
      | 'projectWorkspace'
      | 'warehousePanel'
      | 'warehouseRequestPanel'
      | 'heavyMachineryPanel'
      | 'ticketsPanel'
      | 'profilePanel'
      | 'system'
      | 'maintenancePanel'
      | 'maintenanceManualsPanel'
      | 'maintenanceServiceOrdersPanel'
      | 'maintenanceOSConsultaPanel'
      | 'preencherOSPanel'
      | 'maintenanceProjectPanel'
      | 'officePanel'
      | 'anotacoesPanel'
      | 'notificationsPanel'
      | 'portariaPanel'
    >('myProjects')
    const panelTitle = ref('Projetos')
    const activeId = ref('')
    const maintenanceProjectId = ref('')
    const prefillType = ref('')
    const teamFilterType = ref('')
    const teamDateFrom = ref('')
    const teamDateTo = ref('')
    const teamUserFilter = ref('')
    const docFilterType = ref('')
    const docDateFrom = ref('')
    const docDateTo = ref('')
    const docUserFilter = ref('')
    const showTeam = ref(false)
    const showNotifications = ref(false)
    const showWhatsNew = ref(false)
    const closeWhatsNew = () => { showWhatsNew.value = false; versionStore.dismiss() }

    // ── Onboarding wizard ─────────────────────────────────────────────────────
    const ONBOARDING_DISMISSED_KEY = 'onboarding:dismissed'
    const showOnboarding = ref(false)
    const checkOnboarding = () => {
      const dismissed = localStorage.getItem(ONBOARDING_DISMISSED_KEY)
      if (dismissed) return
      if (['admin-master', 'admin-obra'].includes(sessionStore.state.user?.role || '')) {
        if (projectsStore.all().length === 0) {
          showOnboarding.value = true
        }
      }
    }
    const dismissOnboarding = () => {
      localStorage.setItem(ONBOARDING_DISMISSED_KEY, '1')
      showOnboarding.value = false
    }
    const onboardingGoTo = (panelName: string) => {
      dismissOnboarding()
      router.push('/' + (panelName === 'manage-system' ? 'gerenciar-sistema' : panelName === 'manage-projects' ? 'gerenciar-projetos' : 'gerenciar-usuarios'))
    }
    const ddsConfirmOpen = ref(false)
    const ddsConfirmId = ref('')
    const ddsConfirmNotificationId = ref('')
    const ddsConfirmName = ref('')
    const ddsPhotoDataUrl = ref('')
    const ddsConfirmError = ref('')
    const ddsVideoRef = ref<HTMLVideoElement | null>(null)
    const selectedDocs = ref<string[]>([])
    const selectedReports = ref<string[]>([])
    const overviewFilters = ref<Record<string, 'relatorios' | 'seguranca'>>({})
    const isNarrowScreen = ref(typeof window !== 'undefined' ? window.innerWidth <= 900 : false)
    const mobileMenuOpen = ref(false)

    const projectMode = ref<'list' | 'form' | 'view'>('list')
    const systemTab = ref<'geral' | 'presencas' | 'atividades'>('geral')
    const projectError = ref('')
    const projectInsights = ref<Record<string, { status: 'em-dia' | 'atrasado'; forecastDate: string; plannedPercent: number; actualPercent: number }>>({})
    const projectForm = ref({
      id: '',
      name: '',
      photoDataUrl: '',
      responsibleUsernames: [''] as string[],
      tempResponsibles: [] as Array<{ username: string; days: string; startDate: string; endDate: string; useSpecificDate: boolean }>
    })
    const selectedProjectId = ref('')

    const confirmDeleteOpen = ref(false)
    const confirmDeleteLabel = ref('')
    const confirmDeleteAction = ref<null | (() => Promise<void>)>(null)
    const confirmDeleteSaving = ref(false)

    const accountError = ref('')
    const showAccountForm = ref(false)
    const accountForm = ref({
      uid: '',
      name: '',
      username: '',
      password: '',
      email: '',
      birthDate: '',
      role: 'user' as AccountRole,
      roleId: '',
      subordinateUsernames: [''] as string[],
      isEditing: false
    })
    const showRoleForm = ref(false)
    const roleError = ref('')
    const roleForm = ref({
      id: '',
      name: '',
      isAdmin: false,
      isAnalyst: false,
      warehouseAccess: 0,
      maintenanceAccess: 0,
      constructionAccess: 1,
      officeAccess: 0,
      portariaAccess: 0,
      subordinateRoleIds: [''] as string[],
      isEditing: false
    })
    const offlineEmployeeError = ref('')
    const showOfflineEmployeeForm = ref(false)
    const offlineEmployeeForm = ref({
      id: '',
      name: '',
      username: '',
      roleId: '',
      photoUrl: '',
      isEditing: false
    })

    const userName = computed(() => sessionStore.state.user?.name || '')
    const userAvatar = computed(() => resolveUrl(sessionStore.state.user?.avatar || ''))
    const currentPath = computed(() => route.path)

    const currentMode = computed<'construcao' | 'admin' | 'manutencao' | 'almoxarifado' | 'escritorio' | 'portaria'>(() => {
      const p = route.path
      if (p.startsWith('/admin')) return 'admin'
      if (p.startsWith('/manutencao')) return 'manutencao'
      if (p.startsWith('/almoxarifado')) return 'almoxarifado'
      if (p.startsWith('/escritorio')) return 'escritorio'
      if (p.startsWith('/portaria')) return 'portaria'
      // /maquinario-pesado sem prefixo é acessado no modo almoxarifado
      // (admin usa /admin/maquinario-pesado, construção usa /geral/maquinario-pesado)
      if (p.startsWith('/maquinario-pesado')) return 'almoxarifado'
      return 'construcao'
    })

    const normalizedActivePath = computed(() => {
      const p = route.path
      if (p.startsWith('/admin/')) return p.slice('/admin'.length)
      if (p.startsWith('/geral/')) return p.slice('/geral'.length)
      if (p === '/admin') return '/visao-geral'
      if (p === '/geral') return '/meus-projetos'
      return p
    })

    const modePrefix = computed(() => currentMode.value === 'admin' ? '/admin' : '/geral')
    const notifications = computed(() => notificationsStore.all())
    const unreadNotifications = computed(() => notificationsStore.unreadCount())
    const ticketPendingCount = computed(() =>
      ticketsStore.all().filter((item) => item.status !== 'resolvido' && item.status !== 'cancelado').length
    )
    const isAdmin = computed(() => sessionStore.state.user?.role === 'admin-master' || sessionStore.state.user?.role === 'admin-obra')
    const isMasterAdmin = computed(() => sessionStore.state.user?.role === 'admin-master')
    const isAnalyst = computed(() => Boolean(sessionStore.state.user?.isAnalyst))
    const warehouseAccessLevel = computed(() => {
      if (isAnalyst.value) return 3
      const roleId = sessionStore.state.user?.roleId || ''
      if (!roleId) return isAdmin.value ? 3 : 0
      const role = roles.value.find((item) => item.id === roleId)
      const level = Number(role?.warehouseAccess || 0)
      if (isAdmin.value) return Math.max(3, level)
      return Math.max(0, Math.min(3, level))
    })
    const canManageWarehouse = computed(() =>
      systemStore.isFeatureEnabled('warehouse') && (warehouseAccessLevel.value >= 2 || isAdmin.value)
    )
    const canRequestWarehouse = computed(() => !canManageWarehouse.value && systemStore.isFeatureEnabled('warehouse') && warehouseAccessLevel.value >= 1)

    const canManageConstruction = computed(() => {
      if (!systemStore.isFeatureEnabled('construction')) return false
      if (isAdmin.value) return true
      const roleId = sessionStore.state.user?.roleId || ''
      if (!roleId) return false
      const role = roles.value.find(item => item.id === roleId)
      return Boolean(role?.constructionAccess)
    })

    // Nível de acesso a Ordens de Serviço (ex-"Modo Manutenção"): 0=sem acesso,
    // 1=ver/executar as próprias OS, 2=criar/designar OS avulsas, 3=gestão completa.
    // Valor legado 1 (era booleano) é tratado como nível 2 para não regredir acesso
    // de quem já usava o módulo antes da reformulação — espelha o backend (index.js).
    const maintenanceAccessLevel = computed(() => {
      if (!systemStore.isFeatureEnabled('maintenance')) return 0
      const maintenanceFeatureEnabled = Boolean(systemStore.state.config.maintenanceEnabled || systemForm.value.maintenanceEnabled)
      if (!maintenanceFeatureEnabled) return 0
      if (isAdmin.value) return 3
      const roleId = sessionStore.state.user?.roleId || ''
      if (!roleId) return 0
      const role = roles.value.find(item => item.id === roleId)
      const raw = Math.max(0, Math.min(3, Number(role?.maintenanceAccess || 0)))
      return raw === 1 ? 2 : raw
    })
    const canManageMaintenance = computed(() => maintenanceAccessLevel.value > 0)

    const canManageOffice = computed(() => {
      if (!systemStore.isFeatureEnabled('office')) return false
      if (isAdmin.value) return true
      const roleId = sessionStore.state.user?.roleId || ''
      if (!roleId) return false
      const role = roles.value.find(item => item.id === roleId)
      return Boolean(role?.officeAccess)
    })

    // Nível de acesso ao modo Portaria: 0=sem acesso, 1=operar, 2=+liberar visitantes,
    // 3=gestão completa (histórico/export) — mesmo molde de maintenanceAccessLevel.
    const portariaAccessLevel = computed(() => {
      if (!systemStore.isFeatureEnabled('portaria')) return 0
      if (isAdmin.value) return 3
      const roleId = sessionStore.state.user?.roleId || ''
      if (!roleId) return 0
      const role = roles.value.find(item => item.id === roleId)
      return Math.max(0, Math.min(3, Number(role?.portariaAccess || 0)))
    })
    const canManagePortaria = computed(() => portariaAccessLevel.value > 0)
    const showIaButton  = computed(() => systemStore.isFeatureEnabled('ia'))
    const showTickets   = computed(() => systemStore.isFeatureEnabled('tickets'))

    const siteName = computed(() => systemForm.value.siteName || 'RelatorioShow')
    const siteLogoUrl = computed(() => systemForm.value.siteLogoUrl || '')
    const logoText = computed(() => {
      const logoType = systemForm.value.logoType || 'default'
      if (logoType === 'default') return 'ObraTycoon by GusMachado'
      if (logoType === 'by-gus') return 'by GusMachado'
      if (logoType === 'custom') return systemForm.value.customLogoText || 'ObraTycoon by GusMachado'
      return 'ObraTycoon by GusMachado'
    })
    const systemPrimaryColor = computed(() => systemForm.value.colors?.primary || '#0f4c81')
    const systemReportColor = computed(() => systemForm.value.colors?.report || systemPrimaryColor.value)
    const currentUid = computed(() => sessionStore.state.user?.uid || '')
    const currentUsername = computed(() => sessionStore.state.user?.username || '')
    const currentSystemId = computed(() => sessionStore.state.user?.systemId || '')
    const sessionUserRole = computed(() => sessionStore.state.user?.role || 'user')
    const hasSubordinates = computed(() => {
      const user = sessionStore.state.user
      const subordinates = Array.isArray(user?.subordinateUsernames) ? user?.subordinateUsernames || [] : []
      return Boolean(user?.hasSubordinates) || subordinates.length > 0
    })

    const allTypes = [
      'Relatorio diario de obra',
      'Relatorio de incidente',
      'Dialogos de seguranca',
      'Permissao de trabalho critico',
      'Relatorio de ocorrido'
    ]

    const normalizeText = (value: unknown) =>
      String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

    const submissionSearchIndex = (item: Submission) => {
      const activities = Array.isArray(item.activities)
        ? item.activities.map((activity) => activity?.description || '')
        : []
      const collaborators = Array.isArray(item.collaborators) ? item.collaborators : []
      const tools = Array.isArray(item.tools) ? item.tools.map(t => t.text) : []
      const observations = Array.isArray(item.observations) ? item.observations.map(o => o.text) : []
      return [
        item.title,
        item.attraction,
        item.reportDate,
        formatDateOnly(item.reportDate || ''),
        item.status,
        item.author?.name,
        item.author?.username,
        observations[0],
        ...activities,
        ...collaborators,
        ...tools,
        ...observations
      ]
        .filter(Boolean)
        .join(' ')
    }

    const matchesSearch = (item: Submission) => {
      const query = normalizeText(searchQuery.value.trim())
      if (!query) return true
      return normalizeText(submissionSearchIndex(item)).includes(query)
    }

    const items = computed(() => {
      const user = sessionStore.state.user
      if (!user) return []
      if (user.role === 'admin-master' || user.role === 'admin-obra') {
        return submissionsStore.all().filter((item) => item.author.name === user.name)
      }
      return submissionsStore.all()
    })

    const filteredItems = computed(() => {
      return items.value.filter((item) => {
        if (!matchesSearch(item)) return false
        if (filterType.value && item.title !== filterType.value) return false
        if (userFilter.value && item.author.name !== userFilter.value) return false
        if (dateFrom.value && item.reportDate < dateFrom.value) return false
        if (dateTo.value && item.reportDate > dateTo.value) return false
        return true
      })
    })

    const userOptions = computed(() => {
      const names = new Set<string>()
      items.value.forEach((item) => {
        if (item.author?.name) names.add(item.author.name)
      })
      return Array.from(names.values()).sort()
    })

    const teamItems = computed(() => {
      const user = sessionStore.state.user
      if (!user) return []
      return submissionsStore.all().filter((item) => {
        if (!matchesSearch(item)) return false
        if (item.author.name === user.name) return false
        if (teamFilterType.value && item.title !== teamFilterType.value) return false
        if (teamUserFilter.value && item.author.name !== teamUserFilter.value) return false
        if (teamDateFrom.value && item.reportDate < teamDateFrom.value) return false
        if (teamDateTo.value && item.reportDate > teamDateTo.value) return false
        return true
      })
    })

    const teamUserOptions = computed(() => {
      const user = sessionStore.state.user
      const names = new Set<string>()
      submissionsStore.all().forEach((item) => {
        if (user && item.author.name === user.name) return
        if (item.author?.name) names.add(item.author.name)
      })
      return Array.from(names.values()).sort()
    })

    const documentsItems = computed(() => {
      return submissionsStore.all().filter((item) => {
        if (!matchesSearch(item)) return false
        if (docFilterType.value && item.title !== docFilterType.value) return false
        if (docUserFilter.value && item.author.name !== docUserFilter.value) return false
        if (docDateFrom.value && item.reportDate < docDateFrom.value) return false
        if (docDateTo.value && item.reportDate > docDateTo.value) return false
        return true
      })
    })

    const docUserOptions = computed(() => {
      const names = new Set<string>()
      submissionsStore.all().forEach((item) => {
        if (item.author?.name) names.add(item.author.name)
      })
      return Array.from(names.values()).sort()
    })

    const accounts = computed(() => accountsStore.all())
    const roles = computed(() => rolesStore.all())
    const offlineEmployees = computed(() => offlineEmployeesStore.all())
    const projects = computed(() => projectsStore.all())
    const selectedProject = computed(() => projectsStore.getById(selectedProjectId.value))
    const maintenanceProject = computed(() => projectsStore.all().find(p => p.id === maintenanceProjectId.value) || null)
    const projectCards = computed(() => {
      return projects.value.map((item) => ({ id: item.id, name: item.name }))
    })
    const expandSubordinateRoleIds = (roleId: string) => {
      const visited = new Set<string>()
      const stack = [...(roles.value.find((role) => role.id === roleId)?.subordinateRoleIds || [])]
      while (stack.length > 0) {
        const current = stack.pop()
        if (!current || visited.has(current)) continue
        visited.add(current)
        const role = roles.value.find((item) => item.id === current)
        ;(role?.subordinateRoleIds || []).forEach((child) => {
          if (!visited.has(child)) stack.push(child)
        })
      }
      return Array.from(visited.values())
    }

    const accountSubordinateOptions = computed(() => {
      const allowedRoleIds = expandSubordinateRoleIds(accountForm.value.roleId)
      return accounts.value
        .filter((acc) => {
          if (!accountForm.value.uid) return allowedRoleIds.includes(acc.roleId)
          if (acc.uid === accountForm.value.uid) return false
          return allowedRoleIds.includes(acc.roleId)
        })
        .map((acc) => ({ username: acc.username, name: acc.name }))
    })
    const subordinateOptions = computed(() => {
      const me = accounts.value.find((acc) => acc.username === currentUsername.value)
      const subordinateUsernames = me?.subordinateUsernames || []
      return accounts.value
        .filter((acc) => subordinateUsernames.includes(acc.username))
        .map((acc) => ({ username: acc.username, name: acc.name }))
    })

    const slugifyProjectName = (value: string) =>
      String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')

    const findProjectBySlug = (slug: string) => {
      const target = String(slug || '').trim().toLowerCase()
      if (!target) return null
      return projects.value.find((item) => slugifyProjectName(item.name) === target) || null
    }

    const logout = () => {
      const systemUsername = sessionStore.state.user?.systemUsername || ''
      sessionStore.logout()
      if (systemUsername) {
        router.push(`/login/${encodeURIComponent(systemUsername)}`)
      } else {
        router.push('/login')
      }
    }
    const updateScreenMode = () => {
      if (typeof window === 'undefined') return
      isNarrowScreen.value = window.innerWidth <= 900
      if (!isNarrowScreen.value) {
        mobileMenuOpen.value = false
      }
    }
    const toggleMobileMenu = () => {
      mobileMenuOpen.value = !mobileMenuOpen.value
    }
    const closeMobileMenu = () => {
      if (isNarrowScreen.value) {
        mobileMenuOpen.value = false
      }
    }
    const goProfile = () => {
      panelTitle.value = 'Meu Perfil'
      panel.value = 'profilePanel'
      if (!route.path.startsWith('/profile')) router.push('/profile/editar')
    }
    const toggleCreate = () => { showCreatePopup.value = !showCreatePopup.value }
    const toggleFilter = () => { showFilter.value = !showFilter.value }
    const toggleTeam = () => { showTeam.value = !showTeam.value }

    const startReport = (type: string) => {
      showCreatePopup.value = false
      closeMobileMenu()
      prefillType.value = type
      activeId.value = ''
      panelTitle.value = type
      panel.value = 'reportForm'
    }

    // ── CRIAR ATIVIDADE ──
    const showAtividadeModal = ref(false)
    const openAtividadeModal = () => {
      showCreatePopup.value = false
      closeMobileMenu()
      showAtividadeModal.value = true
    }
    const goCreateAtividade = (projectId: string) => {
      showAtividadeModal.value = false
      const project = projects.value.find((p: { id: string }) => p.id === projectId)
      if (project) {
        viewProject(projectId)
        // Navigate with query so ProjectWorkspaceView opens gantt + task form
        router.push({ path: `/project/${projectId}`, query: { newTask: '1' } })
      }
    }

    // ── REGISTRAR PENDÊNCIA ──
    const showPendenciaModal = ref(false)
    const pendenciaError = ref('')
    const pendenciaSaving = ref(false)
    const pendenciaPhotoDragOver = ref(false)
    const pendenciaPhotoInputRef = ref<HTMLInputElement | null>(null)
    const pendenciaForm = ref({
      projectId: '',
      projectName: '',
      responsavel: '',
      shortDescription: '',
      fullDescription: '',
      photo: ''
    })
    const openPendenciaModal = () => {
      showCreatePopup.value = false
      closeMobileMenu()
      // Pre-select current project if inside one
      if (panel.value === 'projectWorkspace') {
        const match = projects.value.find((p: { name: string }) => p.name === panelTitle.value) as { id: string; name: string } | undefined
        if (match) {
          pendenciaForm.value.projectId = match.id
          pendenciaForm.value.projectName = match.name
        }
      }
      pendenciaError.value = ''
      showPendenciaModal.value = true
    }
    const closePendenciaModal = () => {
      showPendenciaModal.value = false
      pendenciaForm.value = { projectId: '', projectName: '', responsavel: '', shortDescription: '', fullDescription: '', photo: '' }
      pendenciaError.value = ''
    }
    const onPendenciaProjectChange = () => {
      const p = projects.value.find((pr: { id: string }) => pr.id === pendenciaForm.value.projectId) as { id: string; name: string } | undefined
      pendenciaForm.value.projectName = p?.name || ''
    }
    const onPendenciaPhoto = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => { pendenciaForm.value.photo = ev.target?.result as string }
      reader.readAsDataURL(file)
      if (pendenciaPhotoInputRef.value) pendenciaPhotoInputRef.value.value = ''
    }
    const onPendenciaPhotoDrop = (e: DragEvent) => {
      pendenciaPhotoDragOver.value = false
      const file = e.dataTransfer?.files?.[0]
      if (!file || !file.type.startsWith('image/')) return
      const reader = new FileReader()
      reader.onload = (ev) => { pendenciaForm.value.photo = ev.target?.result as string }
      reader.readAsDataURL(file)
    }
    const submitPendencia = async () => {
      if (!pendenciaForm.value.projectId) { pendenciaError.value = 'Selecione um projeto.'; return }
      if (!pendenciaForm.value.shortDescription.trim()) { pendenciaError.value = 'Descrição curta é obrigatória.'; return }
      pendenciaSaving.value = true
      pendenciaError.value = ''
      try {
        await apiFetch(`/projects/${encodeURIComponent(pendenciaForm.value.projectId)}/pendencias`, {
          method: 'POST',
          body: JSON.stringify({
            responsavel: pendenciaForm.value.responsavel,
            shortDescription: pendenciaForm.value.shortDescription,
            fullDescription: pendenciaForm.value.fullDescription,
            photo: pendenciaForm.value.photo,
            projectName: pendenciaForm.value.projectName,
            taskId: '',
            taskTitle: ''
          })
        })
        uiStore.toast('Pendência registrada', 'success')
        closePendenciaModal()
      } catch (err) {
        pendenciaError.value = `${err}`
      } finally {
        pendenciaSaving.value = false
      }
    }

    const goDialogo = () => {
      showCreatePopup.value = false
      closeMobileMenu()
      activeId.value = ''
      panelTitle.value = 'Dialogos de seguranca'
      panel.value = 'dialogForm'
    }
    const goPermissao = () => {
      showCreatePopup.value = false
      closeMobileMenu()
      panelTitle.value = 'Permissao de trabalho critico'
      panel.value = 'permissao'
    }
    const goOcorrido = () => {
      showCreatePopup.value = false
      closeMobileMenu()
      activeId.value = ''
      panelTitle.value = 'Relatorio de ocorrido'
      panel.value = 'occurrenceForm'
    }

    const setFilter = (type: string) => {
      filterType.value = type
      userFilter.value = ''
      selectedReports.value = []
      currentTitle.value = type || 'Todos os relatorios'
      panel.value = 'list'
      closeMobileMenu()
      if (!route.path.includes('/meus-relatorios')) {
        router.push('/geral/meus-relatorios')
      }
    }

    const onSearchChange = (value: string) => {
      searchQuery.value = value
    }

    const onSearchSubmit = () => {
      // A busca ja e reativa. Esse handler existe para manter
      // o comportamento de submit entre os componentes.
    }

    const refreshSubmissions = () => {
      submissionsStore.refresh().catch(() => {
        // ignore
      })
    }

    const refreshAccounts = () => {
      accountsStore.refresh().then(() => {
        const session = sessionStore.state.user
        if (!session) return
        const updated = accountsStore.all().find((acc) => acc.username === session.username)
        if (!updated) return
        // Preserve the "keep logged in" flag — without this, the session migrates
        // to sessionStorage and the user is logged out on browser restart.
        const keepLoggedIn = sessionStore.isPersistent.value
        sessionStore.login({
          name: updated.name,
          role: updated.role,
          username: updated.username,
          systemId: updated.systemId,
          systemUsername: updated.systemUsername || session.systemUsername,
          avatar: updated.avatar,
          uid: updated.uid,
          roleId: updated.roleId,
          subordinateUsernames: updated.subordinateUsernames,
          hasSubordinates: updated.hasSubordinates,
          isAnalyst: Boolean(updated.isAnalyst)
        }, keepLoggedIn)
      }).catch(() => {
        accountError.value = 'Falha ao carregar contas.'
      })
    }

    const refreshRoles = () => {
      rolesStore.refresh().catch(() => {
        roleError.value = 'Falha ao carregar cargos.'
      })
    }

    const refreshOfflineEmployees = () => {
      offlineEmployeesStore.refresh().catch(() => {
        offlineEmployeeError.value = 'Falha ao carregar funcionarios nao logados.'
      })
    }

    const refreshProjects = () => {
      projectsStore.refresh().then(() => {
        const next: Record<string, 'relatorios' | 'seguranca'> = { ...overviewFilters.value }
        projectCards.value.forEach((item) => {
          if (!next[item.id]) next[item.id] = 'relatorios'
        })
        overviewFilters.value = next
        refreshProjectInsights()
        checkOnboarding()
      }).catch(() => {
        projectError.value = 'Falha ao carregar projetos.'
      })
    }

    const refreshProjectInsights = async () => {
      const today = new Date().toISOString().slice(0, 10)
      const toTime = (value: string) => {
        const text = `${value || ''}`.trim()
        if (!text) return NaN
        return new Date(`${text}T00:00:00`).getTime()
      }
      const daysBetween = (from: string, to: string) => {
        const fromTs = toTime(from)
        const toTs = toTime(to)
        if (!Number.isFinite(fromTs) || !Number.isFinite(toTs) || toTs < fromTs) return 0
        return Math.max(1, Math.floor((toTs - fromTs) / 86400000) + 1)
      }
      const clampPercent = (value: number) => Math.max(0, Math.min(100, value))
      const entries = await Promise.all(
        projects.value.map(async (project) => {
          try {
            const ganttResp = await apiFetch<{ tasks: Array<{
              plannedStart?: string
              plannedEnd?: string
              status?: string
              progressUpdates?: Array<{ progressPercent?: number }>
            }> }>(`/projects/${encodeURIComponent(project.id)}/gantt`)
            const rows = ganttResp?.tasks ?? []

            const plannedEnds = rows.map((r) => `${r.plannedEnd || ''}`.trim()).filter(Boolean).sort()
            const forecastDate = plannedEnds.length > 0 ? plannedEnds[plannedEnds.length - 1] : ''

            const delayed = rows.some((row) => {
              const plannedEnd = `${row.plannedEnd || ''}`.trim()
              const status = `${row.status || ''}`.trim()
              if (!plannedEnd || plannedEnd >= today) return false
              if (status === 'concluida') return false
              // considera concluída se progress atingiu 100%
              const updates = row.progressUpdates || []
              const lastPct = updates.length ? Number(updates[updates.length - 1].progressPercent ?? 0) : 0
              if (lastPct >= 100) return false
              return true
            })

            // Planejado: quanto % do projeto deveria estar feito até hoje
            // = soma dos dias planejados que já passaram / total de dias planejados
            let plannedDaysTotal = 0
            let plannedDaysDone = 0
            for (const row of rows) {
              const start = `${row.plannedStart || ''}`.trim()
              const end = `${row.plannedEnd || ''}`.trim()
              if (!start || !end) continue
              const duration = daysBetween(start, end)
              if (duration <= 0) continue
              plannedDaysTotal += duration
              const startTs = toTime(start)
              const endTs = toTime(end)
              const todayTs = toTime(today)
              if (todayTs >= endTs) plannedDaysDone += duration
              else if (todayTs > startTs) plannedDaysDone += Math.floor((todayTs - startTs) / 86400000)
            }
            const plannedPercent = plannedDaysTotal > 0
              ? clampPercent((plannedDaysDone / plannedDaysTotal) * 100)
              : 0

            // Real: média ponderada do progressPercent mais recente de cada tarefa
            let actualTotal = 0
            let actualDone = 0
            for (const row of rows) {
              const start = `${row.plannedStart || ''}`.trim()
              const end = `${row.plannedEnd || ''}`.trim()
              const weight = daysBetween(start, end) || 1
              const updates = row.progressUpdates || []
              const lastUpdate = updates.length ? updates[updates.length - 1] : null
              const pct = Number(lastUpdate?.progressPercent ?? 0)
              actualTotal += weight
              actualDone += (pct / 100) * weight
            }
            const actualPercent = actualTotal > 0 ? clampPercent((actualDone / actualTotal) * 100) : 0

            return [project.id, { status: delayed ? 'atrasado' : 'em-dia', forecastDate, plannedPercent, actualPercent }] as const
          } catch (_err) {
            return [project.id, { status: 'em-dia', forecastDate: '', plannedPercent: 0, actualPercent: 0 }] as const
          }
        })
      )
      projectInsights.value = Object.fromEntries(entries)
    }

    const refreshAllData = () => {
      refreshSubmissions()
      refreshAccounts()
      refreshRoles()
      refreshOfflineEmployees()
      refreshProjects()
    }

    const normalizeHex = (value: string, fallback: string) => {
      const v = `${value || ''}`.trim()
      const withHash = v.startsWith('#') ? v : `#${v}`
      return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash : fallback
    }

    const hexToRgb = (hex: string) => {
      const raw = hex.replace('#', '')
      const int = parseInt(raw, 16)
      return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 }
    }

    const rgbToHex = (r: number, g: number, b: number) =>
      `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')).join('')}`

    const rgbToHsl = (r: number, g: number, b: number) => {
      const rn = r / 255
      const gn = g / 255
      const bn = b / 255
      const max = Math.max(rn, gn, bn)
      const min = Math.min(rn, gn, bn)
      const delta = max - min
      let h = 0
      const l = (max + min) / 2
      let s = 0
      if (delta !== 0) {
        s = delta / (1 - Math.abs(2 * l - 1))
        if (max === rn) h = ((gn - bn) / delta) % 6
        else if (max === gn) h = (bn - rn) / delta + 2
        else h = (rn - gn) / delta + 4
        h *= 60
        if (h < 0) h += 360
      }
      return { h, s, l }
    }

    const hslToRgb = (h: number, s: number, l: number) => {
      const c = (1 - Math.abs(2 * l - 1)) * s
      const hp = h / 60
      const x = c * (1 - Math.abs((hp % 2) - 1))
      let r1 = 0
      let g1 = 0
      let b1 = 0
      if (hp >= 0 && hp < 1) {
        r1 = c
        g1 = x
      } else if (hp < 2) {
        r1 = x
        g1 = c
      } else if (hp < 3) {
        g1 = c
        b1 = x
      } else if (hp < 4) {
        g1 = x
        b1 = c
      } else if (hp < 5) {
        r1 = x
        b1 = c
      } else {
        r1 = c
        b1 = x
      }
      const m = l - c / 2
      return {
        r: (r1 + m) * 255,
        g: (g1 + m) * 255,
        b: (b1 + m) * 255
      }
    }

    const luminance = (hex: string) => {
      const { r, g, b } = hexToRgb(hex)
      const a = [r, g, b].map((v) => {
        const x = v / 255
        return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4
      })
      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2]
    }

    const mixHex = (a: string, b: string, weight = 0.5) => {
      const c1 = hexToRgb(a)
      const c2 = hexToRgb(b)
      return rgbToHex(
        c1.r * (1 - weight) + c2.r * weight,
        c1.g * (1 - weight) + c2.g * weight,
        c1.b * (1 - weight) + c2.b * weight
      )
    }

    const computeAutoSecondary = (primaryHex: string) => {
      const primary = normalizeHex(primaryHex, '#4f3300')
      const { r, g, b } = hexToRgb(primary)
      const hsl = rgbToHsl(r, g, b)
      const complementaryHue = (hsl.h + 180) % 360
      const sat = Math.max(0.35, Math.min(0.78, hsl.s * 0.65 + 0.2))
      const light = 0.88
      const rgb = hslToRgb(complementaryHue, sat, light)
      let secondary = normalizeHex(rgbToHex(rgb.r, rgb.g, rgb.b), '#f3ede3')

      const lumDiff = Math.abs(luminance(primary) - luminance(secondary))
      if (lumDiff < 0.45) {
        secondary = normalizeHex(mixHex(secondary, '#ffffff', 0.2), '#f3ede3')
      }
      return secondary
    }

    const applySystemTheme = (config: SystemConfig) => {
      const primary = normalizeHex(config.colors.primary, '#0f4c81')
      const root = document.documentElement

      // Navbar: primary color, text is always white for dark primaries
      root.style.setProperty('--system-navbar', primary)
      const navbarText = luminance(primary) > 0.35 ? '#201f1e' : '#ffffff'
      root.style.setProperty('--system-navbar-text', navbarText)

      // Sidebar: significantly darkened primary (M365/Teams dark sidebar style)
      const primLum = luminance(primary)
      let sidebarBg: string
      let sidebarText: string
      let sidebarActive: string
      let sidebarHover: string

      if (primLum < 0.35) {
        // Dark primary → very dark sidebar (navy-like)
        sidebarBg = mixHex(primary, '#000000', 0.38)
        sidebarText = 'rgba(255, 255, 255, 0.88)'
        sidebarActive = 'rgba(255, 255, 255, 0.14)'
        sidebarHover = 'rgba(255, 255, 255, 0.07)'
      } else {
        // Light primary → light sidebar
        sidebarBg = mixHex(primary, '#ffffff', 0.88)
        sidebarText = '#201f1e'
        sidebarActive = 'rgba(0, 0, 0, 0.1)'
        sidebarHover = 'rgba(0, 0, 0, 0.05)'
      }

      root.style.setProperty('--system-sidebar', sidebarBg)
      root.style.setProperty('--system-sidebar-text', sidebarText)
      root.style.setProperty('--system-sidebar-muted',
        primLum < 0.35 ? 'rgba(255, 255, 255, 0.52)' : 'rgba(0, 0, 0, 0.45)')
      root.style.setProperty('--system-sidebar-active', sidebarActive)
      root.style.setProperty('--system-sidebar-hover', sidebarHover)

      // Search box in navbar
      root.style.setProperty('--system-search-bg',
        luminance(primary) < 0.35 ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)')
      root.style.setProperty('--system-search-border',
        luminance(primary) < 0.35 ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.2)')
    }

    const refreshSystem = async () => {
      systemError.value = ''
      systemSuccess.value = ''
      try {
        const config = await systemStore.refresh()
        systemForm.value = JSON.parse(JSON.stringify(config))
        applySystemTheme(config)
      } catch (err: unknown) {
        systemError.value = toErrorMessage(err, 'Falha ao carregar configuracoes do sistema.')
      }
    }

    const openSystem = () => {
      if (!isMasterAdmin.value) return
      panelTitle.value = 'Gerenciar Sistema'
      panel.value = 'system'
      systemTab.value = 'geral'
      closeMobileMenu()
      refreshSystem()
      router.push('/admin/gerenciar-sistema')
    }

    const openMaintenance = () => {
      panelTitle.value = 'Manutenção'
      panel.value = 'maintenancePanel'
      closeMobileMenu()
      if (route.path !== '/manutencao') router.push('/manutencao')
    }

    const openMaintenanceManuals = () => {
      panelTitle.value = 'Manuais'
      panel.value = 'maintenanceManualsPanel'
      closeMobileMenu()
      if (route.path !== '/manutencao/manuais') router.push('/manutencao/manuais')
    }

    const openMaintenanceServiceOrders = () => {
      panelTitle.value = 'Gerenciar OS'
      panel.value = 'maintenanceServiceOrdersPanel'
      closeMobileMenu()
      if (route.path !== '/manutencao/ordens-servico') router.push('/manutencao/ordens-servico')
    }

    const openMaintenanceOSConsulta = () => {
      panelTitle.value = 'Consultar OS'
      panel.value = 'maintenanceOSConsultaPanel'
      closeMobileMenu()
      if (route.path !== '/manutencao/consultar-os') router.push('/manutencao/consultar-os')
    }

    const openWarehouseMode = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      closeMobileMenu()
      if (!route.path.startsWith('/almoxarifado')) router.push('/almoxarifado')
    }

    const officeTab = ref('')
    const setOfficeTab = (tab: string) => {
      officeTab.value = tab
      openOfficeMode()
    }

    // Questionários tem URL própria (/escritorio/questionario); as demais abas ficam em /escritorio
    const officeTabPath = (tab: string) => (tab === 'questionarios' ? '/escritorio/questionario' : '/escritorio')

    const openOfficeMode = () => {
      panelTitle.value = 'Escritório'
      panel.value = 'officePanel'
      closeMobileMenu()
      const target = officeTabPath(officeTab.value)
      if (route.path !== target) router.push(target)
    }

    const openPortariaMode = () => {
      panelTitle.value = 'Portaria'
      panel.value = 'portariaPanel'
      closeMobileMenu()
      if (!route.path.startsWith('/portaria')) router.push('/portaria')
    }

    const openGeral = () => {
      panelTitle.value = 'Projetos'
      panel.value = 'myProjects'
      projectMode.value = 'list'
      closeMobileMenu()
      refreshProjects()
      router.push('/geral/meus-projetos')
    }

    const openAdminMode = () => {
      panelTitle.value = 'Visao Geral'
      panel.value = 'overview'
      closeMobileMenu()
      refreshAllData()
      router.push('/admin/visao-geral')
    }

    const handleCreateAction = (action: string, payload?: string) => {
      showCreatePopup.value = false
      closeMobileMenu()
      if (action === 'start-report') {
        startReport(payload || '')
      } else if (action === 'start-atividade') {
        openAtividadeModal()
      } else if (action === 'start-pendencia') {
        openPendenciaModal()
      } else if (action === 'start-manut-incidente') {
        startReport('Relatorio de incidente')
      } else if (action === 'maintenance-create-os') {
        maintenanceStore.state.pendingAction = 'create'
        openMaintenanceServiceOrders()
      } else if (action === 'maintenance-add-os-item' || action === 'maintenance-designate-os') {
        openMaintenanceServiceOrders()
      } else if (action === 'warehouse-request-item') {
        openWarehouseRequestItem()
      } else if (action === 'warehouse-nfe-import') {
        openWarehouseNfeImport()
      } else if (action === 'office-tab') {
        setOfficeTab(payload || 'fornecedores')
      }
    }

    const updateSystemForm = (payload: { field: string; value: string | string[] }) => {
      systemSuccess.value = ''
      if (payload.field.startsWith('colors.')) {
        const colorKey = payload.field.replace('colors.', '') as keyof SystemConfig['colors']
        const rawValue = String(payload.value || '')
        const isValidHex = /^#?[0-9A-Fa-f]{6}$/.test(rawValue.trim())
        const nextColorValue = isValidHex
          ? normalizeHex(rawValue, colorKey === 'primary' ? '#4f3300' : '#f3ede3')
          : rawValue
        const nextColors = {
          ...systemForm.value.colors,
          [colorKey]: nextColorValue
        }
        if (colorKey === 'primary' && systemForm.value.autoSecondary && isValidHex) {
          nextColors.secondary = computeAutoSecondary(nextColorValue)
        }
        systemForm.value = {
          ...systemForm.value,
          colors: nextColors
        }
        return
      }
      if (payload.field === 'autoSecondary') {
        const nextAuto = payload.value === 'true'
        systemForm.value = {
          ...systemForm.value,
          autoSecondary: nextAuto,
          colors: {
            ...systemForm.value.colors,
            secondary: nextAuto ? computeAutoSecondary(systemForm.value.colors.primary) : systemForm.value.colors.secondary
          }
        }
        return
      }
      if (payload.field === 'usePrimaryLoginBackground') {
        systemForm.value = {
          ...systemForm.value,
          usePrimaryLoginBackground: payload.value === 'true' || payload.value === true
        }
        return
      }
      if (payload.field === 'maintenanceEnabled') {
        systemForm.value = {
          ...systemForm.value,
          maintenanceEnabled: payload.value === true || payload.value === 'true'
        }
        return
      }
      if (payload.field === 'systemUsername') {
        systemForm.value = {
          ...systemForm.value,
          systemUsername: String(payload.value || '')
            .toLowerCase()
            .replace(/[^a-z]/g, '')
        }
        return
      }
      if (payload.field === 'workedWeekdays') {
        systemForm.value = {
          ...systemForm.value,
          workedWeekdays: Array.isArray(payload.value) ? payload.value : []
        }
        return
      }
      systemForm.value = {
        ...systemForm.value,
        [payload.field]: payload.value
      }
    }

    const saveSystem = async () => {
      systemError.value = ''
      systemSuccess.value = ''
      isSystemSaving.value = true
      try {
        const primary = normalizeHex(systemForm.value.colors.primary, '#4f3300')
        const secondary = systemForm.value.autoSecondary
          ? computeAutoSecondary(primary)
          : normalizeHex(systemForm.value.colors.secondary, '#f3ede3')
        await systemStore.save({
          ...systemForm.value,
          colors: {
            primary,
            secondary
          }
        })
        await refreshSystem()
        systemSuccess.value = 'Configuracoes salvas com sucesso'
      } catch (err: unknown) {
        systemError.value = toErrorMessage(err, 'Falha ao salvar configuracoes.')
      } finally {
        isSystemSaving.value = false
      }
    }

    const setOverviewFilter = (payload: { projectId: string; value: 'relatorios' | 'seguranca' | string }) => {
      overviewFilters.value = {
        ...overviewFilters.value,
        [payload.projectId]: payload.value === 'seguranca' ? 'seguranca' : 'relatorios'
      }
    }

    const openOverview = () => {
      panelTitle.value = 'Visao Geral'
      panel.value = 'overview'
      closeMobileMenu()
      refreshAllData()
      router.push('/admin/visao-geral')
    }

    const openDocuments = () => {
      panelTitle.value = 'Consulta de Documentos'
      panel.value = 'documents'
      selectedDocs.value = []
      closeMobileMenu()
      router.push('/admin/consulta-documentos')
    }

    const openDrawings = () => {
      panelTitle.value = 'Arquivos'
      panel.value = 'drawings'
      closeMobileMenu()
      router.push(modePrefix.value + '/desenhos')
    }

    const openProjects = () => {
      panelTitle.value = isAnalyst.value ? 'Visualizar Projetos' : 'Gerenciar Projetos'
      panel.value = 'projects'
      projectMode.value = 'list'
      projectError.value = ''
      closeMobileMenu()
      refreshAllData()
      router.push('/admin/gerenciar-projetos')
    }

    const openUsers = () => {
      panelTitle.value = isAnalyst.value ? 'Visualizar Usuarios' : 'Usuarios'
      panel.value = 'users'
      closeMobileMenu()
      refreshAllData()
      router.push('/admin/gerenciar-usuarios')
    }

    const openMyProjects = () => {
      panelTitle.value = 'Projetos'
      panel.value = 'myProjects'
      projectMode.value = 'list'
      projectError.value = ''
      closeMobileMenu()
      refreshProjects()
      refreshAccounts()
      router.push('/geral/meus-projetos')
    }

    const openMyTasks = () => {
      panelTitle.value = 'Minhas Tarefas'
      panel.value = 'myTasks'
      closeMobileMenu()
      refreshProjects()
      refreshAccounts()
      router.push('/geral/minhas-tarefas')
    }

    const openWarehouseStock = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado?tab=estoque')
    }

    const openWarehouseRequests = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado?tab=solicitacoes')
    }

    const openWarehousePlanejamento = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado?tab=planejamento')
    }

    const openWarehouseAvarias = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado/avarias')
    }

    const openWarehouseNfeImport = () => {
      panelTitle.value = 'Almoxarifado'
      panel.value = 'warehousePanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado/importar-nf')
    }

    const warehouseTab = computed(() => String(route.query.tab || ''))

    const openWarehouseRequestItem = () => {
      panelTitle.value = 'Solicitar Item'
      panel.value = 'warehouseRequestPanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/almoxarifado/solicitar-item')
    }

    const openHeavyMachinery = () => {
      panelTitle.value = 'Gerenciar Maquinário'
      panel.value = 'heavyMachineryPanel'
      const prefix = currentMode.value === 'almoxarifado' ? '' : modePrefix.value
      router.push(prefix + '/maquinario-pesado')
    }

    const openTickets = () => {
      panelTitle.value = 'Chamados'
      panel.value = 'ticketsPanel'
      router.push(modePrefix.value + '/chamados')
    }

    const openActivityLog = () => {
      panelTitle.value = 'Gerenciar Sistema'
      panel.value = 'system'
      systemTab.value = 'atividades'
      closeMobileMenu()
      refreshSystem()
      router.push('/admin/log-atividades')
    }

    // Track where the user was before opening a view/form, so back restores correctly
    const returnPanel = ref('list')
    const returnPath = ref('/meus-relatorios')

    const openView = (id: string) => {
      const item = submissionsStore.getById(id)
      if (!item) return
      returnPanel.value = panel.value
      returnPath.value = route.path
      activeId.value = id
      if (item.category === 'dialogo' || item.category === 'ocorrido') {
        panelTitle.value = item.category === 'dialogo' ? 'Dialogos de seguranca' : 'Relatorio de ocorrido'
        panel.value = 'safetyView'
        return
      }
      if (item.category === 'permissao') {
        panelTitle.value = 'Permissao de trabalho critico'
        panel.value = 'permissao'
        return
      }
      panelTitle.value = 'Relatorio'
      panel.value = 'reportView'
    }

    const openEdit = (id: string) => {
      const item = submissionsStore.getById(id)
      if (!item) return
      returnPanel.value = panel.value
      returnPath.value = route.path
      activeId.value = id
      if (item.category === 'dialogo') {
        panelTitle.value = 'Dialogos de seguranca'
        panel.value = 'dialogForm'
        return
      }
      if (item.category === 'ocorrido') {
        panelTitle.value = 'Relatorio de ocorrido'
        panel.value = 'occurrenceForm'
        return
      }
      if (item.category === 'permissao') {
        panelTitle.value = 'Permissao de trabalho critico'
        panel.value = 'permissao'
        return
      }
      panelTitle.value = 'Relatorio'
      panel.value = 'reportForm'
    }

    const backToList = () => {
      panel.value = returnPanel.value
      activeId.value = ''
      prefillType.value = ''
      panelTitle.value = currentTitle.value
      refreshSubmissions()
      if (route.path !== returnPath.value) {
        router.push(returnPath.value)
      }
    }

    const applyPanelFromPath = (rawPath: string) => {
      // Normalize: strip /admin or /geral prefix so matching works uniformly
      let path = rawPath
      if (rawPath === '/admin') path = '/visao-geral'
      else if (rawPath === '/geral') path = '/meus-projetos'
      else if (rawPath.startsWith('/admin/')) path = rawPath.slice('/admin'.length)
      else if (rawPath.startsWith('/geral/')) path = rawPath.slice('/geral'.length)

      const projectMatch = path.match(/^\/([^/]+)\/projeto\/([^/]+?)(?:\/[^/]+)?$/)
      if (projectMatch) {
        const projectSlug = decodeURIComponent(projectMatch[2] || '')
        projectsStore.refresh().then(() => {
          const project = findProjectBySlug(projectSlug)
          if (!project) return
          selectedProjectId.value = project.id
          panelTitle.value = project.name
          panel.value = 'projectWorkspace'
        }).catch(() => {
          // ignore project route refresh errors
        })
        refreshAccounts()
        return
      }
      if (path === '/home' || path === '/meus-projetos') {
        panelTitle.value = 'Projetos'
        panel.value = 'myProjects'
        projectMode.value = 'list'
        refreshProjects()
        refreshAccounts()
        return
      }
      if (path === '/visao-geral' && (isAdmin.value || isAnalyst.value)) {
        panelTitle.value = 'Visao Geral'
        panel.value = 'overview'
        refreshAllData()
        return
      }
      if (path === '/consulta-documentos' && (isAdmin.value || isAnalyst.value)) {
        panelTitle.value = 'Consulta de Documentos'
        panel.value = 'documents'
        selectedDocs.value = []
        return
      }
      if (path === '/desenhos') {
        panelTitle.value = 'Arquivos'
        panel.value = 'drawings'
        return
      }
      if (path === '/gerenciar-projetos' && (isAdmin.value || isAnalyst.value)) {
        panelTitle.value = isAnalyst.value ? 'Visualizar Projetos' : 'Gerenciar Projetos'
        panel.value = 'projects'
        projectMode.value = 'list'
        refreshAllData()
        return
      }
      if (path === '/gerenciar-usuarios' && (isAdmin.value || isAnalyst.value)) {
        panelTitle.value = isAnalyst.value ? 'Visualizar Usuarios' : 'Usuarios'
        panel.value = 'users'
        refreshAllData()
        return
      }
      if (path === '/gerenciar-sistema' && isMasterAdmin.value) {
        panelTitle.value = 'Gerenciar Sistema'
        panel.value = 'system'
        systemTab.value = 'geral'
        refreshSystem()
        return
      }
      if (path === '/meus-projetos') {
        panelTitle.value = 'Projetos'
        panel.value = 'myProjects'
        projectMode.value = 'list'
        refreshProjects()
        refreshAccounts()
        return
      }
      if (path === '/meus-relatorios') {
        panel.value = 'list'
        panelTitle.value = currentTitle.value || 'Todos os relatorios'
        refreshSubmissions()
        return
      }
      if (path.startsWith('/almoxarifado')) {
        if (path.startsWith('/almoxarifado/solicitar-item')) {
          panelTitle.value = 'Solicitar Item'
          panel.value = 'warehouseRequestPanel'
          return
        }
        panelTitle.value = 'Almoxarifado'
        panel.value = 'warehousePanel'
        return
      }
      if (path.startsWith('/escritorio')) {
        panelTitle.value = 'Escritório'
        panel.value = 'officePanel'
        if (path.startsWith('/escritorio/questionario')) officeTab.value = 'questionarios'
        else if (officeTab.value === 'questionarios') officeTab.value = 'fornecedores'
        return
      }
      if (path.startsWith('/portaria')) {
        panelTitle.value = 'Portaria'
        panel.value = 'portariaPanel'
        return
      }
      if (path.startsWith('/chamados')) {
        panelTitle.value = 'Chamados'
        panel.value = 'ticketsPanel'
        return
      }
      if (path === '/log-atividades') {
        panelTitle.value = 'Gerenciar Sistema'
        panel.value = 'system'
        systemTab.value = 'atividades'
        refreshSystem()
        return
      }
      if (path === '/maquinario-pesado') {
        panelTitle.value = 'Gerenciar Maquinário'
        panel.value = 'heavyMachineryPanel'
        return
      }
      if (path === '/manutencao') {
        panelTitle.value = 'Manutenção'
        panel.value = 'maintenancePanel'
        return
      }
      if (path === '/manutencao/manuais') {
        panelTitle.value = 'Manuais'
        panel.value = 'maintenanceManualsPanel'
        return
      }
      if (path === '/manutencao/ordens-servico') {
        panelTitle.value = 'Gerenciar OS'
        panel.value = 'maintenanceServiceOrdersPanel'
        return
      }
      if (path === '/manutencao/consultar-os') {
        panelTitle.value = 'Consultar OS'
        panel.value = 'maintenanceOSConsultaPanel'
        return
      }
      if (path.startsWith('/preencher-os/')) {
        panelTitle.value = 'Preencher OS'
        panel.value = 'preencherOSPanel'
        return
      }
      const maintProjMatch = path.match(/^\/manutencao\/(.+)$/)
      if (maintProjMatch) {
        const projectId = decodeURIComponent(maintProjMatch[1])
        maintenanceProjectId.value = projectId
        panelTitle.value = 'Manutenção'
        panel.value = 'maintenanceProjectPanel'
        if (projectsStore.all().length === 0) projectsStore.refresh().catch(() => { /* ignore */ })
        return
      }
      if (path === '/profile' || path.startsWith('/profile/')) {
        panelTitle.value = 'Meu Perfil'
        panel.value = 'profilePanel'
        return
      }
      if (path === '/anotacoes') {
        panelTitle.value = 'Anotações'
        panel.value = 'anotacoesPanel'
        return
      }
      if (path === '/notificacoes') {
        panelTitle.value = 'Notificações'
        panel.value = 'notificationsPanel'
        return
      }
      panel.value = 'myProjects'
      panelTitle.value = 'Projetos'
    }

    const resetAccountForm = () => {
      accountForm.value = {
        uid: '',
        name: '',
        username: '',
        password: '',
        email: '',
        birthDate: '',
        role: 'user',
        roleId: roles.value.find((role) => role.name.toLowerCase() === 'usuario')?.id || '',
        subordinateUsernames: [''],
        isEditing: false
      }
      accountError.value = ''
    }

    const onAccountFormUpdate = (payload: { field: string; value: FormFieldValue; index?: number }) => {
      if (payload.field === 'subordinateUsernames') {
        const next = [...accountForm.value.subordinateUsernames]
        if (typeof payload.index === 'number') {
          next[payload.index] = String(payload.value || '')
        }
        accountForm.value = {
          ...accountForm.value,
          subordinateUsernames: next
        }
        return
      }
      accountForm.value = {
        ...accountForm.value,
        [payload.field]: payload.value
      }
    }

    const addAccountSubordinate = () => {
      accountForm.value = {
        ...accountForm.value,
        subordinateUsernames: [...accountForm.value.subordinateUsernames, '']
      }
    }

    const removeAccountSubordinate = (index: number) => {
      const next = accountForm.value.subordinateUsernames.filter((_, idx) => idx !== index)
      accountForm.value = {
        ...accountForm.value,
        subordinateUsernames: next.length ? next : ['']
      }
    }

    const openAccountForm = () => {
      if (isAnalyst.value) return
      resetAccountForm()
      showAccountForm.value = true
    }

    const closeAccountForm = () => {
      showAccountForm.value = false
      resetAccountForm()
    }

    const editAccount = (account: Account) => {
      if (isAnalyst.value) return
      accountForm.value = {
        uid: account.uid,
        name: account.name,
        username: account.username,
        password: '',
        email: account.email || '',
        birthDate: '',
        role: account.role,
        roleId: account.roleId || '',
        subordinateUsernames:
          account.subordinateUsernames && account.subordinateUsernames.length > 0
            ? [...account.subordinateUsernames]
            : [''],
        isEditing: true
      }
      accountError.value = ''
      showAccountForm.value = true
    }

    const saveAccount = async () => {
      if (isAnalyst.value) return
      accountError.value = ''
      if (!accountForm.value.name.trim() || !accountForm.value.username.trim()) {
        accountError.value = 'Preencha nome e username.'
        return
      }
      if (!accountForm.value.isEditing && !accountForm.value.password.trim()) {
        accountError.value = 'Informe uma senha para criar a conta.'
        return
      }
      // data de nascimento: obrigatória ao criar; na edição só se preenchida
      if (!accountForm.value.isEditing || accountForm.value.birthDate) {
        const birthErr = birthDateError(accountForm.value.birthDate)
        if (birthErr) { accountError.value = birthErr; return }
      }
      try {
        const selectedRole = roles.value.find((roleItem) => roleItem.id === accountForm.value.roleId)
        let resolvedRole: AccountRole = selectedRole?.isAdmin ? 'admin-obra' : 'user'
        if (isMasterAdmin.value && accountForm.value.role === 'admin-master') {
          resolvedRole = 'admin-master'
        } else if (isMasterAdmin.value && accountForm.value.role === 'admin-obra') {
          resolvedRole = 'admin-obra'
        }
        const subordinateUsernames = accountForm.value.subordinateUsernames
          .map((value) => value.trim())
          .filter(Boolean)
        await accountsStore.upsert({
          uid: accountForm.value.uid,
          name: accountForm.value.name.trim(),
          username: accountForm.value.username.trim(),
          password: accountForm.value.password.trim() || undefined,
          role: resolvedRole,
          roleId: accountForm.value.roleId,
          subordinateUsernames,
          hasSubordinates: subordinateUsernames.length > 0,
          avatar: '',
          email: accountForm.value.email.trim(),
          birthDate: accountForm.value.birthDate
        })
        closeAccountForm()
        refreshAccounts()
      } catch (err: unknown) {
        accountError.value = toErrorMessage(err, 'Falha ao salvar.')
      }
    }

    const resetOfflineEmployeeForm = () => {
      offlineEmployeeForm.value = {
        id: '',
        name: '',
        username: '',
        roleId: '',
        photoUrl: '',
        isEditing: false
      }
      offlineEmployeeError.value = ''
    }

    const openOfflineEmployeeForm = () => {
      if (isAnalyst.value) return
      resetOfflineEmployeeForm()
      showOfflineEmployeeForm.value = true
    }

    const closeOfflineEmployeeForm = () => {
      showOfflineEmployeeForm.value = false
      resetOfflineEmployeeForm()
    }

    const editOfflineEmployee = (employee: OfflineEmployee) => {
      if (isAnalyst.value) return
      offlineEmployeeForm.value = {
        id: employee.id,
        name: employee.name,
        username: employee.username,
        roleId: employee.roleId || '',
        photoUrl: employee.photoUrl || '',
        isEditing: true
      }
      offlineEmployeeError.value = ''
      showOfflineEmployeeForm.value = true
    }

    const onOfflineEmployeeFormUpdate = (payload: { field: string; value: FormFieldValue }) => {
      offlineEmployeeForm.value = {
        ...offlineEmployeeForm.value,
        [payload.field]: payload.value
      }
    }

    const saveOfflineEmployee = async () => {
      if (isAnalyst.value) return
      offlineEmployeeError.value = ''
      if (!offlineEmployeeForm.value.name.trim() || !offlineEmployeeForm.value.username.trim()) {
        offlineEmployeeError.value = 'Preencha nome e username interno.'
        return
      }
      try {
        await offlineEmployeesStore.save({
          id: offlineEmployeeForm.value.id,
          name: offlineEmployeeForm.value.name.trim(),
          username: offlineEmployeeForm.value.username.trim(),
          roleId: offlineEmployeeForm.value.roleId,
          photoUrl: offlineEmployeeForm.value.photoUrl
        })
        closeOfflineEmployeeForm()
        refreshOfflineEmployees()
      } catch (err: unknown) {
        offlineEmployeeError.value = toErrorMessage(err, 'Falha ao salvar funcionario.')
      }
    }

    const askDeleteOfflineEmployee = (employee: OfflineEmployee) => {
      confirmDeleteLabel.value = `funcionario "${employee.name}"`
      confirmDeleteOpen.value = true
      confirmDeleteAction.value = async () => {
        await offlineEmployeesStore.remove(employee.id)
        refreshOfflineEmployees()
      }
    }

    const resetRoleForm = () => {
      roleForm.value = {
        id: '',
        name: '',
        isAdmin: false,
        isAnalyst: false,
        warehouseAccess: 0,
        maintenanceAccess: 0,
        constructionAccess: 1,
        officeAccess: 0,
        portariaAccess: 0,
        subordinateRoleIds: [''],
        isEditing: false
      }
      roleError.value = ''
    }

    const openRoleForm = () => {
      if (isAnalyst.value) return
      resetRoleForm()
      showRoleForm.value = true
    }

    const closeRoleForm = () => {
      showRoleForm.value = false
      resetRoleForm()
    }

    const editRole = (role: Role) => {
      if (isAnalyst.value) return
      roleForm.value = {
        id: role.id,
        name: role.name,
        isAdmin: Boolean(role.isAdmin),
        isAnalyst: Boolean(role.isAnalyst),
        warehouseAccess: Number(role.warehouseAccess || 0),
        maintenanceAccess: Number(role.maintenanceAccess || 0),
        constructionAccess: role.constructionAccess !== undefined && role.constructionAccess !== null ? Number(role.constructionAccess) : 1,
        officeAccess: Number(role.officeAccess || 0),
        portariaAccess: Number(role.portariaAccess || 0),
        subordinateRoleIds: role.subordinateRoleIds.length ? [...role.subordinateRoleIds] : [''],
        isEditing: true
      }
      roleError.value = ''
      showRoleForm.value = true
    }

    const onRoleFormUpdate = (payload: { field: string; value: FormFieldValue; index?: number }) => {
      if (payload.field === 'subordinateRoleIds') {
        const next = [...roleForm.value.subordinateRoleIds]
        if (typeof payload.index === 'number') {
          next[payload.index] = String(payload.value || '')
        }
        roleForm.value = {
          ...roleForm.value,
          subordinateRoleIds: next
        }
        return
      }
      roleForm.value = {
        ...roleForm.value,
        [payload.field]: payload.value
      }
    }

    const addRoleSubordinate = () => {
      roleForm.value = {
        ...roleForm.value,
        subordinateRoleIds: [...roleForm.value.subordinateRoleIds, '']
      }
    }

    const removeRoleSubordinate = (index: number) => {
      const next = roleForm.value.subordinateRoleIds.filter((_, idx) => idx !== index)
      roleForm.value = {
        ...roleForm.value,
        subordinateRoleIds: next.length ? next : ['']
      }
    }

    const saveRole = async () => {
      if (isAnalyst.value) return
      roleError.value = ''
      const subordinateRoleIds = roleForm.value.subordinateRoleIds.map((value) => value.trim()).filter(Boolean)
      if (!roleForm.value.name.trim()) {
        roleError.value = 'Informe o nome do cargo.'
        return
      }
      try {
        await rolesStore.upsert({
          id: roleForm.value.isEditing ? roleForm.value.id : undefined,
          name: roleForm.value.name.trim(),
          isAdmin: Boolean(roleForm.value.isAdmin),
          isAnalyst: Boolean(roleForm.value.isAnalyst),
          warehouseAccess: Math.max(0, Math.min(3, Number(roleForm.value.warehouseAccess || 0))),
          maintenanceAccess: Number(roleForm.value.maintenanceAccess || 0),
          constructionAccess: roleForm.value.constructionAccess !== undefined && roleForm.value.constructionAccess !== null ? Number(roleForm.value.constructionAccess) : 1,
          officeAccess: Number(roleForm.value.officeAccess || 0),
          portariaAccess: Math.max(0, Math.min(3, Number(roleForm.value.portariaAccess || 0))),
          subordinateRoleIds
        })
        closeRoleForm()
        refreshRoles()
      } catch (err: unknown) {
        roleError.value = toErrorMessage(err, 'Falha ao salvar cargo.')
      }
    }

    const activitiesText = (item: Submission) => {
      const list = item.activities?.map((activity) => activity.description).filter(Boolean) || []
      return list.length ? list.join(' | ') : 'Sem atividades'
    }

    const toggleDoc = (id: string) => {
      if (selectedDocs.value.includes(id)) {
        selectedDocs.value = selectedDocs.value.filter((item) => item !== id)
        return
      }
      selectedDocs.value = [...selectedDocs.value, id]
    }

    const toggleReportSelection = (id: string) => {
      if (selectedReports.value.includes(id)) {
        selectedReports.value = selectedReports.value.filter((item) => item !== id)
        return
      }
      selectedReports.value = [...selectedReports.value, id]
    }

    const { exportSelectedDocsAsDoc: exportSelectedReportsAsDoc, exportSelectedDocsAsPdf: exportSelectedReportsAsPdf } = useReportExport({
      selectedDocs: selectedReports,
      documentsItems: filteredItems,
      formatDateOnly,
      siteName,
      systemColor: systemReportColor,
      logoText,
    })

    const { exportSelectedDocsAsDoc, exportSelectedDocsAsPdf } = useReportExport({
      selectedDocs,
      documentsItems,
      formatDateOnly,
      siteName,
      systemColor: systemReportColor,
      logoText,
    })

    // Wrappers that inject per-submission image config from the shared edit state cache
    const exportSelectedDocsAsPdfWithState = async (lang?: string) => {
      const ids = selectedDocs.value
      await exportSelectedDocsAsPdf(lang, submissionEditState.getMergedSizeFactors(ids), submissionEditState.getMergedPaired(ids))
    }
    const exportSelectedReportsAsPdfWithState = async (lang?: string) => {
      const ids = selectedReports.value
      await exportSelectedReportsAsPdf(lang, submissionEditState.getMergedSizeFactors(ids), submissionEditState.getMergedPaired(ids))
    }

    // Backward-compatible alias for stale HMR/template references.
    const exportSelectedDocs = exportSelectedDocsAsDoc

    const removeSelectedDocs = async () => {
      if (!selectedDocs.value.length) return
      const ids = [...selectedDocs.value]
      for (const id of ids) {
        await submissionsStore.deleteSubmission(id)
      }
      selectedDocs.value = []
      refreshSubmissions()
    }

    const removeSelectedReports = async () => {
      if (!selectedReports.value.length) return
      const ids = [...selectedReports.value]
      for (const id of ids) {
        await submissionsStore.deleteSubmission(id)
      }
      selectedReports.value = []
      refreshSubmissions()
    }

    const askDeleteSelectedDocs = () => {
      if (!selectedDocs.value.length) return
      const total = selectedDocs.value.length
      confirmDeleteLabel.value = `${total} documento(s) selecionado(s)`
      confirmDeleteAction.value = async () => {
        await removeSelectedDocs()
      }
      confirmDeleteOpen.value = true
    }

    const askDeleteSelectedReports = () => {
      if (!selectedReports.value.length) return
      const total = selectedReports.value.length
      confirmDeleteLabel.value = `${total} relatório(s) selecionado(s)`
      confirmDeleteAction.value = async () => {
        await removeSelectedReports()
      }
      confirmDeleteOpen.value = true
    }

    const removeAccount = async (account: Account) => {
      if (account.uid === currentUid.value) return
      await accountsStore.remove(account.uid)
      await accountsStore.refresh()
    }

    const askDeleteAccount = (account: Account) => {
      if (isAnalyst.value) return
      if (account.uid === currentUid.value) return
      confirmDeleteLabel.value = account.name || account.username
      confirmDeleteAction.value = async () => {
        await removeAccount(account)
        await accountsStore.refresh()
      }
      confirmDeleteOpen.value = true
    }

    const overviewItems = (projectId: string) => {
      const project = projectCards.value.find((item) => item.id === projectId)
      if (!project) return []
      const type = overviewFilters.value[projectId] || 'relatorios'
      const isSafety = (title: string) =>
        title === 'Dialogos de seguranca' ||
        title === 'Permissao de trabalho critico' ||
        title === 'Relatorio de ocorrido'
      return submissionsStore.all().filter((item) => {
        if (!matchesSearch(item)) return false
        if (item.attraction !== project.name) return false
        if (type === 'seguranca') return isSafety(item.title)
        return !isSafety(item.title)
      })
    }

    const onProjectFormUpdate = (payload: { field: string; value: FormFieldValue; index?: number }) => {
      if (payload.field === 'responsibleUsernames') {
        const next = [...projectForm.value.responsibleUsernames]
        if (typeof payload.index === 'number') {
          next[payload.index] = String(payload.value || '')
        }
        projectForm.value = {
          ...projectForm.value,
          responsibleUsernames: next
        }
        return
      }
      projectForm.value = {
        ...projectForm.value,
        [payload.field]: payload.value
      }
    }

    const openProjectForm = () => {
      if (isAnalyst.value) return
      projectForm.value = { id: '', name: '', photoDataUrl: '', responsibleUsernames: [''], tempResponsibles: [] }
      projectError.value = ''
      projectMode.value = 'form'
    }

    const closeProjectMode = () => {
      projectMode.value = 'list'
      projectError.value = ''
      selectedProjectId.value = ''
      projectForm.value = { id: '', name: '', photoDataUrl: '', responsibleUsernames: [''], tempResponsibles: [] }
    }

    const viewProject = (id: string) => {
      selectedProjectId.value = id
      projectMode.value = 'view'
      panel.value = isAdmin.value ? 'projects' : 'myProjects'
    }

    const extendProject = (id: string) => {
      const project = projectsStore.getById(id)
      if (!project) return
      const projectSlug = slugifyProjectName(project.name)
      const systemUsername = sessionStore.state.user?.systemUsername || 'obra'
      selectedProjectId.value = id
      panel.value = 'projectWorkspace'
      panelTitle.value = project.name
      router.push(`/${encodeURIComponent(systemUsername)}/projeto/${encodeURIComponent(projectSlug)}`)
    }

    const backToProjectsPanel = () => {
      panel.value = 'myProjects'
      panelTitle.value = 'Projetos'
      router.push('/meus-projetos')
    }

    const refreshNotifications = () => {
      notificationsStore.refresh().catch(() => {
        // ignore notification errors
      })
    }

    const goToAllNotifications = () => {
      showNotifications.value = false
      router.push('/notificacoes')
    }

    const toggleNotifications = () => {
      showNotifications.value = !showNotifications.value
      if (showNotifications.value) {
        refreshNotifications()
      }
    }

    let ddsStream: MediaStream | null = null
    const stopDdsCamera = () => {
      if (ddsStream) {
        ddsStream.getTracks().forEach((track) => track.stop())
        ddsStream = null
      }
      if (ddsVideoRef.value) {
        ddsVideoRef.value.srcObject = null
      }
    }

    const startDdsCamera = async () => {
      ddsConfirmError.value = ''
      try {
        stopDdsCamera()
        ddsStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false
        })
        if (ddsVideoRef.value) {
          ddsVideoRef.value.srcObject = ddsStream
        }
      } catch (_err) {
        ddsConfirmError.value = 'Nao foi possivel acessar a camera.'
      }
    }

    const captureDdsPhoto = () => {
      if (!ddsVideoRef.value) return
      const video = ddsVideoRef.value
      const width = video.videoWidth || 640
      const height = video.videoHeight || 360
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) return
      ctx.drawImage(video, 0, 0, width, height)
      ddsPhotoDataUrl.value = canvas.toDataURL('image/jpeg', 0.9)
    }

    const closeDdsConfirm = () => {
      ddsConfirmOpen.value = false
      ddsConfirmId.value = ''
      ddsConfirmNotificationId.value = ''
      ddsConfirmName.value = ''
      ddsPhotoDataUrl.value = ''
      ddsConfirmError.value = ''
      stopDdsCamera()
    }

    const openNotification = async (item: { id: string; type: string; relatedType: string; relatedId: string; title: string }) => {
      await notificationsStore.markRead(item.id)

      if (item.relatedType === 'dds' && item.relatedId) {
        ddsConfirmOpen.value = true
        ddsConfirmId.value = item.relatedId
        ddsConfirmNotificationId.value = item.id
        ddsConfirmName.value = item.title || 'DDS'
        ddsPhotoDataUrl.value = ''
        ddsConfirmError.value = ''
        await nextTick()
        await startDdsCamera()
        return
      }

      if (item.relatedType === 'task' && item.relatedId) {
        try {
          const loc = await apiFetch<{ projectId: string; projectName: string }>(`/tasks/${encodeURIComponent(item.relatedId)}/location`)
          if (loc?.projectId) {
            await projectsStore.refresh()
            extendProject(loc.projectId)
          }
        } catch (_) {
          // task may have been deleted; just close the panel
        }
        return
      }

      if (item.relatedType === 'ticket' && item.relatedId) {
        openTickets()
        return
      }

      if (item.relatedType === 'warehouse-user-request') {
        openWarehouseRequestItem()
        return
      }

      if (item.relatedType === 'warehouse-item' || item.relatedType === 'warehouse-movement') {
        panelTitle.value = 'Almoxarifado'
        panel.value = 'warehousePanel'
        router.push(modePrefix.value + '/almoxarifado')
        return
      }
    }

    const confirmDdsPresence = async () => {
      ddsConfirmError.value = ''
      if (!ddsPhotoDataUrl.value) {
        ddsConfirmError.value = 'Capture uma foto em tempo real para confirmar.'
        return
      }
      try {
        await apiFetch(`/dds/${encodeURIComponent(ddsConfirmId.value)}/confirm`, {
          method: 'POST',
          body: JSON.stringify({
            photoDataUrl: ddsPhotoDataUrl.value
          })
        })
        if (ddsConfirmNotificationId.value) {
          await notificationsStore.markRead(ddsConfirmNotificationId.value)
        } else {
          await notificationsStore.refresh()
        }
        closeDdsConfirm()
      } catch (err: unknown) {
        ddsConfirmError.value = toErrorMessage(err, 'Falha ao confirmar presenca.')
      }
    }

    const markNotificationAsRead = async (id: string) => {
      await notificationsStore.markRead(id)
    }

    const editProject = (id: string) => {
      if (isAnalyst.value) return
      projectError.value = ''
      projectMode.value = 'form'

      // Se id é vazia, é um novo projeto
      if (!id) {
        selectedProjectId.value = ''
        projectForm.value = {
          id: '',
          name: '',
          photoDataUrl: '',
          responsibleUsernames: [''],
          tempResponsibles: []
        }
        return
      }

      // Caso contrário, carrega o projeto existente
      const item = projectsStore.getById(id)
      if (!item) return
      selectedProjectId.value = id
      projectForm.value = {
        id: item.id,
        name: item.name,
        photoDataUrl: item.photoDataUrl || '',
        responsibleUsernames:
          item.responsibleUsernames && item.responsibleUsernames.length > 0
            ? [...item.responsibleUsernames]
            : item.ownerUsername
              ? [item.ownerUsername]
              : [''],
        tempResponsibles: (item.tempResponsibles || []).map((entry) => ({
          username: entry.username || '',
          days: '',
          startDate: entry.startDate || '',
          endDate: entry.endDate || '',
          useSpecificDate: true
        }))
      }
    }

    const addResponsibleUser = () => {
      projectForm.value = {
        ...projectForm.value,
        responsibleUsernames: [...projectForm.value.responsibleUsernames, '']
      }
    }

    const removeResponsibleUser = (index: number) => {
      const next = projectForm.value.responsibleUsernames.filter((_, idx) => idx !== index)
      projectForm.value = {
        ...projectForm.value,
        responsibleUsernames: next.length > 0 ? next : ['']
      }
    }

    const addTempResponsible = () => {
      projectForm.value = {
        ...projectForm.value,
        tempResponsibles: [
          ...projectForm.value.tempResponsibles,
          { username: '', days: '', startDate: '', endDate: '', useSpecificDate: false }
        ]
      }
    }

    const removeTempResponsible = (index: number) => {
      projectForm.value = {
        ...projectForm.value,
        tempResponsibles: projectForm.value.tempResponsibles.filter((_, idx) => idx !== index)
      }
    }

    const updateTempResponsible = (payload: { index: number; field: string; value: string | boolean }) => {
      const next = [...projectForm.value.tempResponsibles]
      const current = next[payload.index]
      if (!current) return
      next[payload.index] = {
        ...current,
        [payload.field]: payload.value
      }
      projectForm.value = {
        ...projectForm.value,
        tempResponsibles: next
      }
    }

    const normalizeTempResponsiblesForSave = () => {
      const today = new Date().toISOString().slice(0, 10)
      return projectForm.value.tempResponsibles
        .map((entry) => {
          const username = entry.username.trim()
          if (!username) return null
          let startDate = entry.startDate
          let endDate = entry.endDate
          if (!entry.useSpecificDate) {
            const days = Number(entry.days || 0)
            if (!Number.isFinite(days) || days <= 0) return null
            const start = new Date(today)
            const end = new Date(today)
            end.setDate(end.getDate() + days)
            startDate = start.toISOString().slice(0, 10)
            endDate = end.toISOString().slice(0, 10)
          }
          if (!startDate || !endDate) return null
          return { username, startDate, endDate, grantedBy: currentUsername.value }
        })
        .filter((entry): entry is { username: string; startDate: string; endDate: string; grantedBy: string } => Boolean(entry))
    }

    const saveProject = async () => {
      if (isAnalyst.value) return
      projectError.value = ''
      const responsibles = projectForm.value.responsibleUsernames
        .map((item) => item.trim())
        .filter(Boolean)
      const uniqueResponsibles = Array.from(new Set(responsibles))
      if (!projectForm.value.name.trim() || uniqueResponsibles.length === 0) {
        projectError.value = 'Preencha nome do projeto e informe pelo menos um responsavel.'
        return
      }
      try {
        await projectsStore.upsert({
          id: projectForm.value.id || undefined,
          name: projectForm.value.name.trim(),
          photoDataUrl: projectForm.value.photoDataUrl || '',
          responsibleUsernames: uniqueResponsibles,
          tempResponsibles: normalizeTempResponsiblesForSave()
        })
        closeProjectMode()
      } catch (err: unknown) {
        projectError.value = toErrorMessage(err, 'Falha ao salvar projeto.')
      }
    }

    const askDeleteProject = (project: Project) => {
      if (isAnalyst.value) return
      confirmDeleteLabel.value = project.name
      confirmDeleteAction.value = async () => {
        try {
          await projectsStore.remove(project.id)
          closeProjectMode()
        } catch (err: unknown) {
          projectError.value = toErrorMessage(err, 'Falha ao excluir projeto.')
        }
      }
      confirmDeleteOpen.value = true
    }

    const cancelDelete = () => {
      confirmDeleteOpen.value = false
      confirmDeleteLabel.value = ''
      confirmDeleteAction.value = null
    }

    const confirmDelete = async () => {
      if (!confirmDeleteAction.value) {
        cancelDelete()
        return
      }
      confirmDeleteSaving.value = true
      try {
        await confirmDeleteAction.value()
      } finally {
        confirmDeleteSaving.value = false
      }
      cancelDelete()
    }

    // ─── SUSPENSION LOGIC ──────────────────────────────────────────────────────
    const reactivationMessage = ref('')
    const reactivationLoading = ref(false)
    const reactivationError = ref('')
    const reactivationSent = ref(false)

    const isSuspendedAdmin = computed(() => {
      const user = sessionStore.state.user
      if (!user) return false
      if (!user.suspended) return false
      return user.role === 'admin-master' || user.role === 'admin-obra'
    })

    const submitReactivation = async () => {
      reactivationError.value = ''
      reactivationLoading.value = true
      try {
        await apiFetch('/reactivation-request', {
          method: 'POST',
          body: JSON.stringify({ message: reactivationMessage.value })
        })
        reactivationSent.value = true
      } catch (err: unknown) {
        reactivationError.value = err instanceof Error ? err.message : 'Erro ao enviar solicitacao.'
      } finally {
        reactivationLoading.value = false
      }
    }
    // ───────────────────────────────────────────────────────────────────────────

    // Messenger
    const messengerOpen = ref(false)
    const toggleMessenger = () => {
      messengerOpen.value = !messengerOpen.value
      if (messengerOpen.value) messengerStore.loadConversations()
    }

    // Storage alert
    const storageAlertPct = ref(0)
    const storageAlertMb = ref(0)
    const storageAlertLimit = ref(0)
    const storageAlertDismissed = ref(false)
    const showStorageAlert = computed(() => !storageAlertDismissed.value && storageAlertPct.value >= 70 && isAdmin.value)

    const checkStorageAlert = async () => {
      if (!isAdmin.value) return
      try {
        const data = await apiFetch<{ usedMb: number; limitMb: number; pct: number }>('/storage-status')
        storageAlertPct.value = data.pct
        storageAlertMb.value = data.usedMb
        storageAlertLimit.value = data.limitMb
      } catch { /* ignore */ }
    }

    let autoRefreshTimer: ReturnType<typeof setInterval> | null = null

    const isAppReady = ref(false)

    onMounted(() => {
      updateScreenMode()
      window.addEventListener('resize', updateScreenMode)

      // Track the 3 most critical stores — show app only when they're ready
      Promise.allSettled([
        systemStore.refresh().catch(() => null),
        systemStore.refreshFlags().catch(() => null),
        projectsStore.refresh().catch(() => null),
        accountsStore.refresh().catch(() => null),
      ]).then(() => {
        isAppReady.value = true
      })

      // Wrappers run in parallel (update derived state: systemForm, overviewFilters, session sync)
      refreshAccounts()
      refreshProjects()

      if (!isSuspendedAdmin.value) {
        refreshSubmissions()
        refreshRoles()
        refreshSystem()
        refreshNotifications()
        checkStorageAlert()
        messengerStore.refreshUnread()
        ticketsStore.refresh().catch(() => {
          // ignore tickets bootstrap error
        })
        applyPanelFromPath(route.path)
        timezoneStore.initFromServer()

        // SSE: refresh Gantt insights when any task changes
        sseOn('task-changed', () => { refreshProjectInsights() })

        // Auto-refresh a cada 60 segundos
        autoRefreshTimer = setInterval(() => {
          refreshProjectInsights()
          refreshNotifications()
          messengerStore.refreshUnread()
        }, 60_000)
      } else {
        refreshSystem()
        applyPanelFromPath(route.path)
      }
    })

    watch(
      () => route.path,
      (nextPath) => {
        applyPanelFromPath(nextPath)
      }
    )

    // Deep link from the "todas as notificações" page: /home?notif=<id> opens it in-context
    watch(
      () => route.query.notif,
      async (idRaw) => {
        const id = typeof idRaw === 'string' ? idRaw : ''
        if (!id) return
        let item = notificationsStore.all().find((n) => n.id === id)
        if (!item) {
          await notificationsStore.refresh().catch(() => null)
          item = notificationsStore.all().find((n) => n.id === id)
        }
        if (item) {
          await openNotification(item)
        }
        const nextQuery = { ...route.query }
        delete nextQuery.notif
        router.replace({ path: route.path, query: nextQuery })
      },
      { immediate: true }
    )

    // Redirect users without construction access to their available mode after roles load
    const stopRoleRedirectWatch = watch(
      () => roles.value.length,
      (n) => {
        if (n === 0) return
        if (!isAdmin.value && !canManageConstruction.value) {
          const path = route.path
          if (canManageWarehouse.value && !path.startsWith('/almoxarifado') && !path.startsWith('/manutencao') && !path.startsWith('/escritorio') && !path.startsWith('/profile')) {
            router.replace('/almoxarifado')
          } else if (canManageMaintenance.value && !path.startsWith('/manutencao') && !path.startsWith('/escritorio') && !path.startsWith('/profile')) {
            router.replace('/manutencao')
          } else if (canManageOffice.value && !path.startsWith('/escritorio') && !path.startsWith('/profile')) {
            router.replace('/escritorio')
          }
        }
        stopRoleRedirectWatch()
      }
    )

    // Enforce feature flags: redirect away from disabled modes
    const enforceFeatureFlags = () => {
      const path = route.path
      const flags = systemStore.state.featureFlags
      if (Object.keys(flags).length === 0) return // not loaded yet

      const safeRedirect = () => {
        if (systemStore.isFeatureEnabled('construction')) { router.replace('/home'); return }
        if (systemStore.isFeatureEnabled('warehouse') && canManageWarehouse.value) { router.replace('/almoxarifado'); return }
        if (systemStore.isFeatureEnabled('maintenance') && canManageMaintenance.value) { router.replace('/manutencao'); return }
        if (systemStore.isFeatureEnabled('office') && canManageOffice.value) { router.replace('/escritorio'); return }
        router.replace('/home')
      }

      if (path.startsWith('/manutencao') && !systemStore.isFeatureEnabled('maintenance')) {
        safeRedirect()
      } else if (path.startsWith('/almoxarifado') && !systemStore.isFeatureEnabled('warehouse')) {
        safeRedirect()
      } else if (path.startsWith('/escritorio') && !systemStore.isFeatureEnabled('office')) {
        safeRedirect()
      } else if (!path.startsWith('/manutencao') && !path.startsWith('/almoxarifado') && !path.startsWith('/escritorio') && !path.startsWith('/admin') && !path.startsWith('/profile') && !path.startsWith('/ia') && !systemStore.isFeatureEnabled('construction')) {
        safeRedirect()
      }
    }

    watch(() => systemStore.state.featureFlags, enforceFeatureFlags, { deep: true })
    watch(() => route.path, enforceFeatureFlags)

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateScreenMode)
      stopDdsCamera()
      if (autoRefreshTimer !== null) clearInterval(autoRefreshTimer)
      sseDisconnect()
    })

    return {
      router,
      userName,
      siteName,
      siteLogoUrl,
      logoText,
      searchQuery,
      userAvatar,
      currentPath,
      notifications,
      unreadNotifications,
      ticketPendingCount,
      showNotifications,
      showOnboarding, dismissOnboarding, onboardingGoTo,
      ddsConfirmOpen,
      ddsConfirmName,
      ddsPhotoDataUrl,
      ddsConfirmError,
      ddsVideoRef,
      isAdmin,
      isMasterAdmin,
      isAnalyst,
      canManageWarehouse,
      canRequestWarehouse,
      canManageConstruction,
      canManageMaintenance,
      maintenanceAccessLevel,
      canManageOffice,
      canManagePortaria,
      portariaAccessLevel,
      openPortariaMode,
      showIaButton,
      showTickets,
      hasSubordinates,
      isNarrowScreen,
      mobileMenuOpen,
      showCreatePopup,
      showFilter,
      filterType,
      userFilter,
      userOptions,
      dateFrom,
      dateTo,
      teamFilterType,
      teamDateFrom,
      teamDateTo,
      teamUserFilter,
      teamUserOptions,
      docFilterType,
      docDateFrom,
      docDateTo,
      docUserFilter,
      docUserOptions,
      showTeam,
      selectedDocs,
      selectedReports,
      overviewFilters,
      currentTitle,
      panel,
      panelTitle,
      activeId,
      prefillType,
      allTypes,
      filteredItems,
      teamItems,
      documentsItems,
      accounts,
      roles,
      offlineEmployees,
      accountSubordinateOptions,
      subordinateOptions,
      projects,
      projectInsights,
      projectCards,
      projectMode,
      projectForm,
      projectError,
      selectedProject,
      confirmDeleteOpen,
      confirmDeleteLabel,
      confirmDeleteSaving,
      accountForm,
      accountError,
      showAccountForm,
      showRoleForm,
      roleForm,
      roleError,
      offlineEmployeeForm,
      offlineEmployeeError,
      showOfflineEmployeeForm,
      logout,
      goProfile,
      toggleNotifications,
      goToAllNotifications,
      openNotification,
      startDdsCamera,
      captureDdsPhoto,
      confirmDdsPresence,
      closeDdsConfirm,
      markNotificationAsRead,
      toggleMobileMenu,
      onSearchChange,
      onSearchSubmit,
      toggleCreate,
      toggleFilter,
      handleCreateAction,
      openMaintenanceManuals,
      openMaintenanceServiceOrders,
      openMaintenanceOSConsulta,
      startReport,
      goDialogo,
      goPermissao,
      goOcorrido,
      setFilter,
      toggleTeam,
      openOverview,
      openDocuments,
      openDrawings,
      openProjects,
      openUsers,
      openSystem,
      openMyProjects,
      openMyTasks,
      openWarehouseStock,
      openWarehouseRequests,
      openWarehousePlanejamento,
      openWarehouseAvarias,
      openWarehouseNfeImport,
      warehouseTab,
      openWarehouseRequestItem,
      openHeavyMachinery,
      openTickets,
      openActivityLog,
      backToProjectsPanel,
      openView,
      openEdit,
      backToList,
      activitiesText,
      overviewItems,
      setOverviewFilter,
      toggleDoc,
      toggleReportSelection,
      exportSelectedDocs,
      exportSelectedDocsAsDoc,
      exportSelectedDocsAsPdf: exportSelectedDocsAsPdfWithState,
      exportSelectedReportsAsDoc,
      exportSelectedReportsAsPdf: exportSelectedReportsAsPdfWithState,
      askDeleteSelectedDocs,
      askDeleteSelectedReports,
      removeSelectedDocs,
      removeSelectedReports,
      askDeleteAccount,
      currentUid,
      sessionUserRole,
      refreshAccounts,
      refreshOfflineEmployees,
      refreshProjects,
      refreshAllData,
      refreshSystem,
      openAccountForm,
      closeAccountForm,
      editAccount,
      saveAccount,
      onAccountFormUpdate,
      addAccountSubordinate,
      removeAccountSubordinate,
      openOfflineEmployeeForm,
      closeOfflineEmployeeForm,
      editOfflineEmployee,
      saveOfflineEmployee,
      onOfflineEmployeeFormUpdate,
      askDeleteOfflineEmployee,
      refreshRoles,
      openRoleForm,
      closeRoleForm,
      editRole,
      saveRole,
      onRoleFormUpdate,
      addRoleSubordinate,
      removeRoleSubordinate,
      openProjectForm,
      closeProjectMode,
      viewProject,
      extendProject,
      editProject,
      saveProject,
      askDeleteProject,
      onProjectFormUpdate,
      addResponsibleUser,
      removeResponsibleUser,
      addTempResponsible,
      removeTempResponsible,
      updateTempResponsible,
      currentUsername,
      currentSystemId,
      confirmDelete,
      cancelDelete,
      isSystemSaving,
      systemForm,
      systemError,
      systemSuccess,
      updateSystemForm,
      saveSystem,
      selectedProjectId,
      formatReportDate: formatDateOnly,
      isSuspendedAdmin,
      reactivationMessage,
      reactivationLoading,
      reactivationError,
      reactivationSent,
      submitReactivation,
      storageAlertPct,
      storageAlertMb,
      storageAlertLimit,
      storageAlertDismissed,
      showStorageAlert,
      messengerOpen,
      messengerStore,
      toggleMessenger,
      showWhatsNew,
      closeWhatsNew,
      versionStore,
      showAtividadeModal,
      openAtividadeModal,
      goCreateAtividade,
      showPendenciaModal,
      pendenciaForm,
      pendenciaError,
      pendenciaSaving,
      pendenciaPhotoDragOver,
      pendenciaPhotoInputRef,
      openPendenciaModal,
      closePendenciaModal,
      onPendenciaProjectChange,
      onPendenciaPhoto,
      onPendenciaPhotoDrop,
      submitPendencia,
      openMaintenance,
      openWarehouseMode,
      officeTab,
      setOfficeTab,
      openOfficeMode,
      openGeral,
      openAdminMode,
      maintenanceProject,
      currentMode,
      normalizedActivePath,
      systemStore,
      isAppReady
    }
  }
})
</script>

<style scoped lang="scss">
.storage-alert-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  font-size: 13px;
  font-weight: 500;
  z-index: 100;

  svg { flex-shrink: 0; }

  span { flex: 1; }
}

.storage-alert-warn {
  background: #fef3c7;
  color: #92400e;
  border-bottom: 1px solid #f59e0b;
}

.storage-alert-critical {
  background: #fef2f2;
  color: #991b1b;
  border-bottom: 1px solid #dc2626;
}

.storage-alert-dismiss {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 4px;
  opacity: 0.7;
  &:hover { opacity: 1; background: rgba(0,0,0,0.08); }
}

.saving-overlay {
  position: fixed;
  inset: 0;
  background: rgba(31, 30, 26, 0.34);
  z-index: 9999;
  display: grid;
  place-items: center;
}

.saving-card {
  background: var(--system-navbar, #0f4c81);
  color: var(--system-navbar-text, #ffffff);
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  font-size: 14px;
  box-shadow: var(--shadow-lg);
}

.home-shell {
  height: 100vh;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.home-body {
  display: grid;
  grid-template-columns: 240px 1fr;
  height: calc(100vh - var(--navbar-h));
  min-height: 0;
  overflow: hidden;
}

.sidebar-shell {
  overflow: hidden;
  height: 100%;
  background: var(--system-sidebar, #0a3460);
}

.content {
  padding: 24px 28px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  color: var(--ink);
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) transparent;
}

.content::-webkit-scrollbar {
  width: 8px;
}

.content::-webkit-scrollbar-track {
  background: transparent;
}

.content::-webkit-scrollbar-thumb {
  background: var(--border-strong);
  border-radius: 999px;
  border: 2px solid transparent;
  background-clip: content-box;
}

.content.workspace-mode {
  overflow: hidden;
  padding: 0;
}
.content.workspace-mode > .panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.content.profile-mode {
  padding: 0;
  overflow: hidden;
}

.content.maintenance-mode {
  padding: 0;
  overflow: hidden;
}

.content.form-mode {
  padding: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.content.form-mode > .panel {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.content-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}


.dds-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 40;
  display: grid;
  place-items: center;
}

.dds-modal {
  width: min(680px, calc(100vw - 28px));
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  display: grid;
  gap: 10px;
}

.dds-video {
  width: 100%;
  min-height: 320px;
  background: #1f1e1a;
  border-radius: 10px;
  object-fit: cover;
}

.dds-preview {
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  border: 1px solid rgba(31, 30, 26, 0.15);
  border-radius: 10px;
}

@media (max-width: 900px) {
  .home-body {
    grid-template-columns: 1fr;
    height: calc(100vh - var(--navbar-h));
  }

  .mobile-sidebar-backdrop {
    position: fixed;
    inset: 0;
    top: var(--navbar-h);
    background: rgba(0, 0, 0, 0.0);
    z-index: 49;
    pointer-events: none;
  }

  .backdrop-fade-enter-active,
  .backdrop-fade-leave-active {
    transition: opacity 0.22s ease;
  }
  .backdrop-fade-enter-from,
  .backdrop-fade-leave-to {
    opacity: 0;
  }

  .sidebar-shell {
    position: fixed;
    top: var(--navbar-h);
    left: 0;
    width: 100vw;
    height: calc(100vh - var(--navbar-h));
    height: calc(100dvh - var(--navbar-h));
    z-index: 50;
    transform: translateX(-100%);
    pointer-events: none;
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
  }

  .sidebar-shell.open {
    transform: translateX(0);
    pointer-events: auto;
  }

  .content {
    padding: 16px;
  }

  .content-header {
    flex-wrap: wrap;
    gap: 10px;
  }
}

// ─── Suspension overlay ────────────────────────────────────────────────────────
.suspension-overlay {
  position: fixed;
  inset: 0;
  background: #f0f4f8;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  font-family: 'Inter', sans-serif;
}

.suspension-panel {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.12);
  padding: 40px 36px;
  max-width: 480px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.suspension-header {
  display: flex;
  align-items: center;
  gap: 14px;
}

.suspension-title {
  font-size: 20px;
  font-weight: 700;
  color: #92400e;
  margin: 0;
}

.suspension-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.55;
}

.suspension-stats {
  display: flex;
  gap: 16px;
}

.susp-stat {
  flex: 1;
  background: #f9fafb;
  border-radius: 10px;
  padding: 14px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.susp-stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #111827;
}

.susp-stat-label {
  font-size: 12px;
  color: #6b7280;
}

.suspension-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.susp-label {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.susp-textarea {
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  resize: vertical;
  outline: none;
  font-family: inherit;
  &:focus { border-color: #0f4c81; }
}

.susp-error {
  font-size: 13px;
  color: #dc2626;
  background: #fef2f2;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 0;
}

.susp-btn {
  background: #0f4c81;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
  &:hover:not(:disabled) { background: #0d3d6a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.susp-success {
  background: #dcfce7;
  color: #166534;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
}

.susp-logout {
  background: transparent;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  align-self: flex-start;
  &:hover { background: #f9fafb; }
}

/* ── Quick Modals (Criar Atividade / Registrar Pendência) ── */
.quick-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}

.quick-modal {
  background: var(--card, #fff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.quick-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border, #e5e7eb);
}

.quick-modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ink, #111);
}

.quick-modal-close {
  background: transparent;
  border: none;
  font-size: 18px;
  color: var(--ink-muted, #6b7280);
  cursor: pointer;
  line-height: 1;
  padding: 2px 6px;
  border-radius: 6px;
  &:hover { background: var(--bg, #f3f4f6); }
}

.quick-modal-body {
  padding: 16px 20px;
  overflow-y: auto;
  flex: 1;
}

.quick-modal-sub {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-muted, #6b7280);
  margin: 0 0 8px;
}

.quick-project-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.quick-project-btn {
  background: var(--bg, #f9fafb);
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 8px;
  padding: 10px 14px;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  color: var(--ink, #111);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  &:hover {
    border-color: var(--accent, #0f4c81);
    background: var(--accent-pale, #e8f0f9);
  }
}

.quick-form-field {
  margin-bottom: 14px;
}

.quick-form-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #111);
  margin-bottom: 5px;
}

.quick-required {
  color: #dc2626;
  margin-left: 2px;
}

.quick-form-input,
.quick-form-select {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 7px;
  font-size: 14px;
  background: var(--bg, #fff);
  color: var(--ink, #111);
  outline: none;
  &:focus { border-color: var(--accent, #0f4c81); }
}

.quick-form-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1.5px solid var(--border, #e5e7eb);
  border-radius: 7px;
  font-size: 14px;
  background: var(--bg, #fff);
  color: var(--ink, #111);
  outline: none;
  resize: vertical;
  min-height: 72px;
  &:focus { border-color: var(--accent, #0f4c81); }
}

.quick-form-file {
  font-size: 13px;
  color: var(--ink-muted, #6b7280);
}

.quick-photo-preview {
  margin-top: 8px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.quick-preview-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid var(--border, #e5e7eb);
}

.quick-photo-remove {
  background: transparent;
  border: none;
  font-size: 12px;
  color: #dc2626;
  cursor: pointer;
  padding: 2px 0;
  &:hover { text-decoration: underline; }
}

.quick-form-error {
  font-size: 13px;
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 8px 12px;
  margin-bottom: 10px;
}

.quick-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 20px 16px;
  border-top: 1px solid var(--border, #e5e7eb);
}

/* image-grid for pendência photo */
.quick-image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px;
  padding: 8px;
  border: 1.5px dashed var(--border, #d1d5db);
  border-radius: 8px;
  min-height: 56px;
  transition: border-color 0.2s, background 0.2s;

  &.is-drag-over {
    border-color: var(--accent, #0f4c81);
    background: rgba(15, 76, 129, 0.04);
  }
}

.quick-image-preview {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0,0,0,0.1);
  aspect-ratio: 1 / 1;

  img { width: 100%; height: 100%; object-fit: cover; }
}

.quick-image-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  &:hover { background: rgba(0,0,0,0.8); }
}

.quick-image-add {
  aspect-ratio: 1 / 1;
  border-radius: 8px;
  border: 1.5px dashed rgba(15, 76, 129, 0.35);
  background: rgba(15, 76, 129, 0.04);
  color: var(--accent, #0f4c81);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  min-height: 56px;
  transition: background 0.14s, border-color 0.14s;

  &:hover {
    background: rgba(15, 76, 129, 0.09);
    border-color: var(--accent, #0f4c81);
  }
}

/* ── App loading overlay ── */
.app-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: var(--bg, #f5f0e8);
  display: grid;
  place-items: center;
}

.app-loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.app-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(15, 76, 129, 0.15);
  border-top-color: var(--system-navbar, #0f4c81);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.app-loading-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-muted, #6b6560);
  letter-spacing: 0.01em;
}

.app-loading-enter-active {
  transition: opacity 0.15s ease;
}
.app-loading-leave-active {
  transition: opacity 0.3s ease;
}
.app-loading-enter-from,
.app-loading-leave-to {
  opacity: 0;
}
</style>

